// src/main/shell/symbolGrid/SymbolGrid.ts
// Single global DOM renderer with a fixed pool of row elements.
// Reads data from SymbolTreeView + a SymbolGridDataSource. Never creates/destroys rows.
//
// PORTED from the agent's SymbolGrid. The 80-row pooled virtual renderer
// (@tanstack/virtual-core), ROW_HEIGHT=34, the columns + `--vg-columns` grid,
// the search toolbar, expand/collapse tree (level*20+12 indentation), Canvas
// sparklines, dirty-flag row diffing, column resize/sort/reorder, and the
// expand/collapse animations are all preserved verbatim.
//
// DECOUPLED + DROPPED vs the agent (per port scope):
//   • data layer: SymbolManager/ControllerManager/DashboardClient → ONE
//     controller-scoped SymbolGridDataSource (no gateway/controller args).
//   • dropped: symbol PROPERTIES, PLC PROGRAMS, dashboard-pinning, write/edit/
//     delete RPCs, add-symbol. So the row pool has NO pin/delete/edit buttons,
//     and the toolbar has no add button.
//   • chart settings: kept as the per-chart THEME picker only (the chart-column
//     header gear → <vitesse-chart-theme-picker>); the agent's other chart-config
//     knobs stay dropped.
//   • kept: symbol tree, live value column, sparklines, search/filter,
//     expand/collapse, columns (resize/sort/reorder), select + bulk subscribe,
//     and inline label editing (routed to the source's optional setLabel).

import { Virtualizer, observeElementOffset, observeElementRect, elementScroll } from '@tanstack/virtual-core';
import type { SymbolGridDataSource, SymbolValue } from './dataSource';
// The gate: what the user may do, from the same annotations the api-gateway reads.
import { getSession } from '@vitesse/base-ui';
import { gridPermissions, type GridPermissions } from './pagedBackend';
import { SymbolTreeView } from './SymbolTreeView';
import { fmtTime } from '@vitesse/base-ui/format';
import { formatAge } from './valueAge';
import { carriesValue, clipCell } from './rowValue';
import type { SymbolGridOptions, SymbolGridToolbarOptions, PooledRow, ViewChangedEvent } from './types';
import {
  drawSparkline,
  clearSparkline,
  drawTooltipOverlay,
  getMinMaxCache,
  getThemeLineColor,
} from './SparklineRenderer';
import type { SparklineConfig } from './SparklineRenderer';
import { nextChartTheme } from './chartUtils';
import {
  createChevronSvg,
  createAlertSvg,
  createLockSvg,
  createTrashSvg,
  createSquarePenSvg,
  createSearchSvg,
  createClearSvg,
  createExpandCollapseSvg,
  createSortArrowSvg,
} from './icons';
import { formatSearchInput } from './search/searchCommands';
import { SearchAutocomplete } from './search/SearchAutocomplete';
import { ColumnResizer } from '@vitesse/base-ui/grid';
import { t, onLocaleChange, formatNumber } from '@vitesse/base-ui/i18n';
import type { BulkProgress, BulkOp } from '../../../core/bridge/connect-client';
import { SEARCH_DEBOUNCE_MS } from '../../../core/ui-constants';
import './chart-theme-picker'; // registers <vitesse-chart-theme-picker> (chart-column settings gear)
import { VitesseValueEditor } from '../vitesse-value-editor'; // registers <vitesse-value-editor> (square-pen)

// Constants
const ROW_HEIGHT = 34;
const POOL_SIZE = 80;
const OVERSCAN_ROWS = 15;

// Cache sentinel poked into a row's type/perm cache to force a re-render of the
// localized cells on a language switch (never equals a real type or permission).
const SENTINEL_DIRTY = 'i18n-dirty';

// Column header i18n keys, keyed by column id — shared by buildHeaderRow (initial
// render) and refreshHeaderI18n (re-label on language change). 'select' is the
// checkbox column (its key is used as the checkbox title, not a header label).
const COL_LABEL_KEYS = {
  select: 'grid.selectAll',
  name: 'grid.colName',
  label: 'grid.colLabel',
  type: 'grid.colType',
  access: 'grid.colAccess',
  value: 'grid.colValue',
  chart: 'grid.colChart',
  actions: 'grid.colActions',
} as const;

// Animation constants
const ANIM_EASING = 'cubic-bezier(0.25, 1, 0.5, 1)';
// Expand is the exact inverse of collapse — same duration + easing, mirrored
// motion (children emerge from behind the parent vs. tuck back into it).
const COLLAPSE_MS = 240;
const EXPAND_MS = COLLAPSE_MS;

export class SymbolGrid {
  // --- Dependencies ---
  private source: SymbolGridDataSource;

  // --- Active controller ---
  private activeView: SymbolTreeView | null = null;
  // True when the data source IS the view (IndexerPagedView — server-linearized,
  // viewport-windowed). Disables the full-client-tree row-flight animation.
  private pagedMode = false;
  private currentControllerId: string = '';
  private isConnected: boolean = true;

  // --- DOM ---
  private container: HTMLElement;
  private valueEditor: VitesseValueEditor | null = null; // lazily created on first square-pen click
  private wrapper: HTMLDivElement;
  private toolbar: HTMLDivElement;
  private headerRow: HTMLDivElement;
  private scrollContainer: HTMLDivElement;
  private emptyState: HTMLDivElement;
  private emptyTitle!: HTMLDivElement;
  private emptyDesc!: HTMLDivElement;

  // --- Row pool ---
  private pool: PooledRow[] = [];

  // --- Virtual scroll ---
  private scrollParent: HTMLElement | null = null;
  private virtualizer: Virtualizer<HTMLElement, Element> | null = null;
  private virtualizerCleanup: (() => void) | null = null;
  private rafId: number = 0;

  // --- Selection ---
  private selectedSymbols: Set<string> = new Set();
  private selectAllActive: boolean = false;
  private deselectedSymbols: Set<string> = new Set();

  // --- Stale value cache (preserves last known formatted values across disconnect/reconnect) ---
  // Bounded LRU: without a cap this accumulates one entry per symbol EVER rendered,
  // so scrolling a 10M-symbol catalog leaks ~one map entry per symbol. The cap is
  // far above the on-screen window, so visible rows always survive; only entries
  // for rows scrolled far away are evicted. setStaleValue() enforces it.
  private static readonly STALE_CAP = 4096;
  private staleValues: Map<string, string> = new Map();

  private setStaleValue(symbolId: string, fmtValue: string): void {
    this.staleValues.delete(symbolId); // re-insert moves to newest (LRU recency)
    this.staleValues.set(symbolId, fmtValue);
    if (this.staleValues.size > SymbolGrid.STALE_CAP) {
      const oldest = this.staleValues.keys().next().value; // Map preserves insertion order
      if (oldest !== undefined) this.staleValues.delete(oldest);
    }
  }

  // --- Editing ---
  private editingRow: { poolRow: PooledRow; symbolId: string } | null = null;
  private _labelNavInProgress = false;
  private _labelNavTargetIndex = -1;

  // --- Toolbar elements ---
  private searchInput!: HTMLInputElement;
  private searchHighlight!: HTMLDivElement;
  private clearSearchBtn!: SVGSVGElement;
  private expandCollapseBtn!: HTMLButtonElement;
  private selectedCountSpan!: HTMLSpanElement;
  /** "Showing the first N — narrow the search" — visible only when the server
   *  reports the window CUT a branch short (truncated_parents). Without it a
   *  cut-short listing reads as complete. Tooltip lists the cut branches. */
  private truncatedChip!: HTMLSpanElement;
  private bulkActionsWrap!: HTMLDivElement;
  /** Each bulk button with the permission it needs, so the bar can hide the ones
   *  the user may not use rather than offering a click the server refuses. */
  private bulkButtons: { btn: HTMLButtonElement; need: 'lock' | 'priority' }[] = [];
  private bulkProgressSpan!: HTMLSpanElement;
  // Label spans + their i18n keys, so a language switch re-translates them.
  private bulkButtonLabels: Array<{ span: HTMLSpanElement; key: 'grid.bulkLock' | 'grid.bulkUnlock' | 'grid.bulkPrioritize' | 'grid.bulkClearPriority' }> = [];
  private bulkInProgress = false;
  private expandCollapseSetExpanded!: (expanded: boolean) => void;
  private searchAutocomplete: SearchAutocomplete | null = null;

  // --- Update loops ---
  private valueUpdateRafId: number = 0;
  private structuralCheckInterval: number | null = null;
  private ageTickInterval: number | null = null;
  private lastVersion: number = 0;

  // --- Column widths as % of total grid width (tracked in JS for resize) ---
  private columnWidths: number[] | null = null;

  // --- Column sort ---
  private sortArrows: Map<string, SVGSVGElement> = new Map();

  // --- Animation state ---
  private animating = false;

  // --- Resize: the blue wall + drag plumbing live in the shared ColumnResizer ---
  private resizer: ColumnResizer | null = null;
  private columnResizing = false; // true during an active wall drag
  private hoverBoundary = -1; // column boundary the cursor is hovering (for the grab affordance), or -1
  private localeUnsub?: () => void; // i18n: re-label the header on language change
  private sessionUnsub?: () => void; // authz: re-render write affordances on plan/identity change

  // --- Sparkline tooltip ---
  private sparklineTooltip: HTMLDivElement | null = null;
  private sparklineDot: HTMLDivElement | null = null;
  private hoveredCanvas: HTMLCanvasElement | null = null;
  private hoverState: { symbolId: string; clientX: number; clientY: number } | null = null;
  // Last cursor position over the grid. Scrolling moves the content under a
  // STATIONARY cursor without firing mousemove, so we re-resolve which sparkline
  // is under the cursor on scroll (pooled canvases recycle to new symbols as you
  // scroll — otherwise the tooltip sticks to the chart that scrolled away).
  private lastPointer: { x: number; y: number } | null = null;
  private hoverScrollRaf = 0; // rAF handle for the throttled scroll → hover refresh

  // --- Scroll persistence ---
  private scrollSaveTimeout: number | null = null;
  private scrollSaveHandler: (() => void) | null = null;
  private lastScrollOffset: number = 0; // cached for detach (DOM may be gone)

  // --- Tooltip ---
  private tooltip: HTMLDivElement | null = null;

  // --- Keyboard shortcut handler (for cleanup) ---
  private keyboardShortcutHandler: ((e: KeyboardEvent) => void) | null = null;

  // --- Selection persistence ---
  private selectionSaveTimeout: number | null = null;
  /** Per-view toolbar config (the view owns its toolbar; no backend flags). */
  private readonly toolbarCfg: SymbolGridToolbarOptions;

  constructor(options: SymbolGridOptions) {
    this.source = options.dataSource;
    this.container = options.container;
    this.toolbarCfg = options.toolbar ?? {};

    // Build DOM skeleton
    this.wrapper = document.createElement('div');
    this.wrapper.className = 'vg-wrapper';

    this.toolbar = this.buildToolbar();
    this.headerRow = this.buildHeaderRow();
    // Shared column-resize chrome (blue wall + drag); the wall spans the header
    // row down to the bottom — same as the log tree.
    this.resizer = new ColumnResizer(this.wrapper, {
      highlightTop: () => this.headerRow.getBoundingClientRect().top - this.wrapper.getBoundingClientRect().top,
    });
    this.localeUnsub = onLocaleChange(() => this.refreshI18n());
    // Same shape as the locale subscription: permissions arrive with the
    // identity (synchronous) but the PLAN lands on a stream, so the priority "!"
    // is fail-open until it does. Re-render when it changes rather than leaving a
    // control visible that the server now refuses.
    this.sessionUnsub = getSession()?.onChange(() => this.refreshPermissions());
    this.scrollContainer = document.createElement('div');
    this.scrollContainer.className = 'vg-scroll-container';

    this.emptyState = document.createElement('div');
    this.emptyState.className = 'vg-empty-state';
    // Filled by updateEmptyState — "nothing here yet" and "nothing MATCHES" are
    // different facts, and showing the first for the second reads as a broken grid.
    this.emptyTitle = document.createElement('div');
    this.emptyTitle.className = 'vg-empty-title';
    this.emptyDesc = document.createElement('div');
    this.emptyDesc.className = 'vg-empty-desc';
    this.emptyState.appendChild(this.emptyTitle);
    this.emptyState.appendChild(this.emptyDesc);

    this.wrapper.appendChild(this.toolbar);
    this.wrapper.appendChild(this.headerRow);
    this.wrapper.appendChild(this.scrollContainer);
    this.wrapper.appendChild(this.emptyState);

    this.container.appendChild(this.wrapper);

    // Build row pool
    this.buildRowPool();

    // Set up event delegation
    this.setupEventDelegation();
  }

  // ============================================================
  // Public API
  // ============================================================

  /**
   * Ensure the wrapper is attached to the given container.
   */
  ensureAttached(container: HTMLElement): void {
    if (this.container !== container || !this.wrapper.parentElement) {
      this.container = container;
      this.scrollParent = null; // Invalidate cached scroll parent
      container.appendChild(this.wrapper);
    }
  }

  /**
   * Mount the grid against its data source's controller. The source is fixed
   * for the life of the grid (one source = one controller), so this is the
   * agent's `switchToController` collapsed to a single, source-implicit view.
   */
  mount(isConnected: boolean = true): void {
    this.isConnected = isConnected;

    // Build the view for this controller. If the source itself implements the
    // tree-view surface (IndexerPagedView — server-linearized, viewport-windowed,
    // stateless), use it directly; otherwise build the client-tree SymbolTreeView
    // over it (legacy/stub sources).
    this.pagedMode = typeof (this.source as unknown as Partial<SymbolTreeView>).getSymbolAtIndex === 'function';
    this.activeView = this.pagedMode
      ? (this.source as unknown as SymbolTreeView)
      : new SymbolTreeView(this.source);
    this.activeView.activate();

    this.currentControllerId = this.source.controllerId;

    // Columns are fixed-order (reorder removed) — only widths persist.
    this.restoreColumnWidths();

    // Restore selection state
    this.clearSelection();
    this.restoreSelection();

    // Update toolbar
    this.searchInput.value = this.activeView.getFilter();
    this.updateSearchHighlight();
    this.updateExpandCollapseIcon();
    this.updateBulkActionBar();
    this.updateSortIndicators();

    // Attach to view
    this.activeView.on('viewChanged', this.onViewChanged);

    // Configure virtualizer (this triggers scroll observation, which fires onChange → renderVisibleRows)
    this.setupVirtualizer();

    // Update empty state
    this.updateEmptyState();

    // Start update loops
    this.startUpdateLoops();

    // The virtualizer's onChange handles rendering after it measures.
    // Force a render next frame as a fallback in case onChange hasn't fired yet.
    requestAnimationFrame(() => {
      this.virtualizer?._willUpdate();
      this.renderVisibleRows();
      this.restoreScrollPosition();
    });
  }

  /** Update connection status (greys out + clears charts on disconnect). */
  updateConnectionStatus(connected: boolean): void {
    const wasConnected = this.isConnected;
    this.isConnected = connected;

    if (wasConnected && !connected) {
      // Went offline — grey out tree and clear charts
      this.wrapper.classList.add('vg-disconnected');
      for (const row of this.pool) {
        if (row.sparklineCanvas.style.display !== 'none') {
          clearSparkline(row.sparklineCanvas);
          row.cache.chartMinMaxVersion = -1;
        }
      }
      // Clear selection — stale on reconnect (symbols may change)
      this.clearSelection();
      this.updateBulkActionBar();
      try {
        localStorage.removeItem(`${this.storagePrefix}_selectedSymbols`);
      } catch {
        /* ignore */
      }
    }

    if (!wasConnected && connected) {
      // Came back online — restore interactive state
      this.wrapper.classList.remove('vg-disconnected');
      this.staleValues.clear(); // Fresh data incoming
      /*
       * AND RE-READ THE CATALOG, because it may not be the same catalog.
       *
       * A PLC goes away for a reason, and the commonest reason is somebody editing it: symbols get
       * added, renamed and deleted while it is down. The open structure stream does push membership
       * changes live — but it was open across a disconnect during which the indexer's view of this
       * controller was rebuilt from scratch, and a window that missed the rebuild keeps painting the
       * rows it last saw. One re-read at the moment of reconnection is the cheap way to be sure the
       * list on screen is the list on the machine.
       *
       * Optional on the source: the stub's client tree mutates locally and has nothing to re-fetch.
       */
      this.source.refetchWindow?.();
    }

    this.renderVisibleRows(); // Refresh button states
  }

  /** Clean up and destroy */
  destroy(): void {
    this.stopUpdateLoops();

    if (this.activeView) {
      this.activeView.off('viewChanged', this.onViewChanged);
      this.activeView.destroy();
      this.activeView = null;
    }

    if (this.virtualizerCleanup) {
      this.virtualizerCleanup();
      this.virtualizerCleanup = null;
    }
    this.virtualizer = null;

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }

    if (this.scrollSaveTimeout) {
      clearTimeout(this.scrollSaveTimeout);
    }
    if (this.selectionSaveTimeout) {
      clearTimeout(this.selectionSaveTimeout);
    }

    if (this.searchAutocomplete) {
      this.searchAutocomplete.destroy();
    }

    this.localeUnsub?.();
    this.sessionUnsub?.();

    if (this.tooltip) {
      this.tooltip.remove();
    }

    if (this.sparklineTooltip) {
      this.sparklineTooltip.remove();
      this.sparklineTooltip = null;
    }
    if (this.sparklineDot) {
      this.sparklineDot.remove();
      this.sparklineDot = null;
    }
    this.hoveredCanvas = null;
    this.hoverState = null;

    if (this.keyboardShortcutHandler) {
      document.removeEventListener('keydown', this.keyboardShortcutHandler);
      this.keyboardShortcutHandler = null;
    }

    this.resizer?.destroy();
    this.resizer = null;

    this.container.innerHTML = '';
  }

  /** Detach from controller (flush scroll, stop loops). */
  detach(): void {
    // Flush cached scroll offset to localStorage (DOM may already be detached)
    this.flushScrollPosition();
    this.stopUpdateLoops();

    if (this.activeView) {
      this.activeView.off('viewChanged', this.onViewChanged);
    }

    // Hide all pool rows
    for (const row of this.pool) {
      row.element.style.transform = 'translateY(-9999px)';
      row.currentSymbolId = null;
      row.currentIndex = -1;
    }
  }

  // ============================================================
  // DOM Construction
  // ============================================================

  private buildToolbar(): HTMLDivElement {
    const toolbar = document.createElement('div');
    toolbar.className = 'vg-toolbar';

    // --- Search ---
    const searchWrap = document.createElement('div');
    searchWrap.className = 'vg-search-wrap';

    const searchIcon = createSearchSvg();
    searchIcon.classList.add('vg-search-icon');
    searchWrap.appendChild(searchIcon);

    this.searchHighlight = document.createElement('div');
    this.searchHighlight.className = 'vg-search-highlight';
    searchWrap.appendChild(this.searchHighlight);

    this.searchInput = document.createElement('input');
    this.searchInput.className = 'vg-search-input';
    this.searchInput.type = 'text';
    this.searchInput.placeholder = t('grid.searchPlaceholder');
    this.searchInput.spellcheck = false;
    this.searchInput.autocomplete = 'off';
    searchWrap.appendChild(this.searchInput);

    this.clearSearchBtn = createClearSvg();
    this.clearSearchBtn.classList.add('vg-search-clear');
    this.clearSearchBtn.style.display = 'none';
    searchWrap.appendChild(this.clearSearchBtn);

    // Keyboard shortcut hint — matches global search <kbd> style
    const shortcutHint = document.createElement('kbd');
    shortcutHint.className = 'vg-search-shortcut';
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    const modSpan = document.createElement('span');
    modSpan.textContent = isMac ? '⌘' : 'Ctrl';
    shortcutHint.appendChild(modSpan);
    shortcutHint.appendChild(document.createTextNode('/'));
    searchWrap.appendChild(shortcutHint);

    // (search is appended AFTER the actions below — actions sit on the LEFT,
    // the search fills the rest of the connected top bar.)

    // --- Actions ---
    const actions = document.createElement('div');
    actions.className = 'vg-toolbar-actions';

    // Selected-symbol count — sits BEFORE the expand/collapse-all control, only
    // visible while bulk-selecting (its display is toggled in updateBulkActionBar).
    this.selectedCountSpan = document.createElement('span');
    this.selectedCountSpan.className = 'vg-selected-count';
    this.selectedCountSpan.style.display = 'none';
    actions.appendChild(this.selectedCountSpan);

    // Truncation chip — a TOOLBAR chip, deliberately not a grid row: injecting a
    // synthetic row would break the pooled-row/virtualizer index math. Hidden
    // until a page reports truncated parents (updateTruncatedChip).
    this.truncatedChip = document.createElement('span');
    this.truncatedChip.className = 'vg-truncated-chip';
    this.truncatedChip.style.display = 'none';
    actions.appendChild(this.truncatedChip);

    this.expandCollapseBtn = this.createToolbarButton('vg-btn-expand', t('grid.collapseAll'));
    const expandCollapseIcon = createExpandCollapseSvg();
    this.expandCollapseSetExpanded = expandCollapseIcon.setExpanded;
    this.expandCollapseBtn.appendChild(expandCollapseIcon.svg);
    actions.appendChild(this.expandCollapseBtn);

    // Bulk actions wrapper — animated slide-in/out. Holds the bulk action buttons
    // (priority, lock). (Subscribe/unsubscribe removed — everything is always
    // subscribed; the selected count now lives before expand/collapse, above.)
    this.bulkActionsWrap = document.createElement('div');
    this.bulkActionsWrap.className = 'vg-bulk-actions';

    // Bulk actions — lock/unlock + prioritize/clear over the whole selection.
    // Chunked + bounded-concurrency in the Backend Worker (off the main thread)
    // so even a select-all 500k operation streams without freezing the UI.
    const mkBulkBtn = (
      icon: SVGSVGElement,
      key: 'grid.bulkLock' | 'grid.bulkUnlock' | 'grid.bulkPrioritize' | 'grid.bulkClearPriority',
      op: 'lock' | 'unlock' | 'prioritize' | 'clearPriority',
    ): HTMLButtonElement => {
      const btn = document.createElement('button');
      // THE base button (compact): "off" actions (unlock, clear-priority) hover
      // red per the icon-button convention (red = turning something off); apply
      // actions hover blue. `vg-bulk-btn` stays as a marker for the running-state
      // dim hook.
      const off = op === 'unlock' || op === 'clearPriority';
      btn.className = `vg-bulk-btn base-button base-button-${off ? 'red' : 'blue'} base-button-sm`;
      btn.appendChild(icon);
      const span = document.createElement('span');
      span.className = 'vg-bulk-btn-label';
      span.textContent = t(key);
      btn.appendChild(span);
      btn.addEventListener('click', () => void this.runBulk(op));
      this.bulkButtonLabels.push({ span, key });
      // lock/unlock need org:symbol:lock; prioritize/clear need the telemetry
      // capability — the same two guards as the per-row padlock and "!".
      this.bulkButtons.push({ btn, need: op === 'lock' || op === 'unlock' ? 'lock' : 'priority' });
      return btn;
    };
    const lockClosed = createLockSvg(); lockClosed.setLocked(true);
    const lockOpen = createLockSvg(); lockOpen.setLocked(false);
    this.bulkActionsWrap.append(
      mkBulkBtn(lockClosed.svg, 'grid.bulkLock', 'lock'),
      mkBulkBtn(lockOpen.svg, 'grid.bulkUnlock', 'unlock'),
      mkBulkBtn(createAlertSvg(), 'grid.bulkPrioritize', 'prioritize'),
      mkBulkBtn(createAlertSvg(), 'grid.bulkClearPriority', 'clearPriority'),
    );

    this.bulkProgressSpan = document.createElement('span');
    this.bulkProgressSpan.className = 'vg-bulk-progress';
    this.bulkActionsWrap.appendChild(this.bulkProgressSpan);

    actions.appendChild(this.bulkActionsWrap);

    // Search fills the LEFT; expand/collapse + bulk sit on the RIGHT.
    toolbar.appendChild(searchWrap);
    toolbar.appendChild(actions);

    // The embedding VIEW owns its toolbar (no backend flags). Omitted fields keep
    // the controller symbol-viewer's full toolbar; a view opts a piece out by passing
    // false. The integration value tree (topic viewport) passes { bulkExpand: false }
    // — search STAYS (FTS works because the tree always materializes), expand/collapse-all
    // GOES (the tree is virtualized + there's no full set to expand). Elements stay BUILT
    // (their refs are touched elsewhere); they're just hidden.
    if (this.toolbarCfg.search === false) searchWrap.style.display = 'none';
    if (this.toolbarCfg.bulkExpand === false) this.expandCollapseBtn.style.display = 'none';

    // --- Initialize search autocomplete ---
    this.searchAutocomplete = new SearchAutocomplete(searchWrap, {
      onSelect: (completion: string) => {
        // Replace from last '/' to end with the completion
        const val = this.searchInput.value;
        const lastSlash = val.lastIndexOf('/');
        const before = lastSlash >= 0 ? val.slice(0, lastSlash) : val;
        this.searchInput.value = before + completion;
        this.updateSearchHighlight();
        this.clearSearchBtn.style.display = this.searchInput.value ? '' : 'none';
        // Trigger filter with debounce
        if (this.activeView) {
          this.activeView.setFilter(this.searchInput.value);
        }
        // Re-focus and put cursor at end
        this.searchInput.focus();
        this.searchInput.setSelectionRange(this.searchInput.value.length, this.searchInput.value.length);
      },
      onClose: () => {
        // Nothing extra needed
      },
      getSymbolData: () => {
        if (!this.currentControllerId) return null;
        return {
          iterate: () => this.source.iterateSymbols(),
          getLabel: (id) => this.source.getLabel(id),
          getValue: (id) => this.source.getValue(id),
        };
      },
    });

    // --- Wire search events ---
    let searchDebounce: ReturnType<typeof setTimeout>;
    this.searchInput.addEventListener('input', () => {
      clearTimeout(searchDebounce);
      this.updateSearchHighlight();
      this.clearSearchBtn.style.display = this.searchInput.value ? '' : 'none';

      // Update autocomplete dropdown
      this.updateAutocomplete();

      // Shared platform debounce (see core/ui-constants.ts for the rationale).
      // Each setFilter reopens the structure stream (streamSeq supersede) and
      // the indexer query is cached server-side, so an extra mid-burst reopen
      // is cheap and harmless.
      searchDebounce = setTimeout(() => {
        if (this.activeView) {
          this.activeView.setFilter(this.searchInput.value);
        }
      }, SEARCH_DEBOUNCE_MS);
    });

    this.searchInput.addEventListener('focus', () => {
      // Show autocomplete if there's a partial command
      this.updateAutocomplete();
    });

    this.searchInput.addEventListener('blur', () => {
      // Slight delay so mousedown on dropdown item fires first
      setTimeout(() => {
        if (this.searchAutocomplete) {
          this.searchAutocomplete.hide();
        }
      }, 150);
    });

    this.clearSearchBtn.addEventListener('mousedown', (e) => {
      e.preventDefault();
      this.searchInput.value = '';
      this.clearSearchBtn.style.display = 'none';
      this.updateSearchHighlight();
      if (this.searchAutocomplete) {
        this.searchAutocomplete.hide();
      }
      if (this.activeView) {
        this.activeView.setFilter('');
      }
      this.searchInput.focus();
    });

    this.expandCollapseBtn.addEventListener('click', () => {
      if (!this.activeView || this.animating) return;
      const expanding = !this.activeView.expandAllMode;
      if (this.pagedMode) {
        // The paged view re-queries the indexer; the row-flight animation needs
        // a complete client tree, so just toggle + let viewChanged re-render.
        this.activeView.setExpandAll(expanding);
        this.updateExpandCollapseIcon();
        return;
      }
      this.animateExpandCollapseAll(expanding);
    });


    return toolbar;
  }

  /** Re-label the header when the language changes. The header is built once, so
   *  update the existing label spans + the select-all title in place (preserves
   *  column order, widths, and the sort-arrow refs). */
  private refreshHeaderI18n(): void {
    for (const cell of Array.from(this.headerRow.children) as HTMLElement[]) {
      const id = cell.dataset.colId as keyof typeof COL_LABEL_KEYS | undefined;
      if (!id || !(id in COL_LABEL_KEYS)) continue;
      if (id === 'select') {
        const cb = cell.querySelector<HTMLInputElement>('.vg-select-all');
        if (cb) cb.title = t(COL_LABEL_KEYS.select);
      } else {
        const span = cell.querySelector<HTMLElement>('.vg-header-label');
        if (span) span.textContent = t(COL_LABEL_KEYS[id]);
      }
    }
  }

  /** Re-label every localized surface on a language switch: header columns,
   *  search placeholder, the bulk-selection count, and the visible rows (so the
   *  'add label' placeholders re-translate). Built-once chrome is patched in
   *  place; rows go through the normal render path. */
  /**
   * Re-render write affordances after the session changed — the plan arrived, or
   * the user switched org.
   *
   * Mirrors refreshI18n: recompute the flags on the source, then force every
   * pooled row through updateRow by invalidating its perm cache, since
   * renderVisibleRows skips rows whose index did not move and a permission change
   * moves nothing.
   */
  private refreshPermissions(): void {
    const src = this.source as { may?: GridPermissions; pluginCanLock?: boolean };
    if (!src?.may) return; // a source that does not declare permissions is ungated
    // The plugin capability is a property of the SOURCE, not the session, so it
    // survives a permission refresh — re-reading it here would need a descriptor
    // handle the grid does not have.
    src.may = gridPermissions(src.pluginCanLock === true);
    for (const row of this.pool) {
      if (row.currentSymbolId === null || row.currentIndex < 0) continue;
      const visible = this.activeView?.getSymbolAtIndex(row.currentIndex);
      if (!visible) continue;
      row.cache.perm = SENTINEL_DIRTY;
      this.updateRow(row, visible, row.currentIndex);
    }
    this.updateBulkActionBar();
  }

  /** Show/hide + word the truncation chip from the active view's report. The
   *  view type is duck-typed here (only the paged view has the method — the
   *  legacy client-tree holds every row and can never truncate). */
  private updateTruncatedChip(visibleCount: number): void {
    if (!this.truncatedChip) return;
    const view = this.activeView as { getTruncatedParents?: () => string[] } | null;
    const cut = view?.getTruncatedParents?.() ?? [];
    if (cut.length === 0) {
      this.truncatedChip.style.display = 'none';
      return;
    }
    this.truncatedChip.textContent = t('grid.truncatedChip', { n: formatNumber(visibleCount) });
    this.truncatedChip.title = cut.join('\n'); // the branches that were cut short
    this.truncatedChip.style.display = '';
  }

  private refreshI18n(): void {
    this.refreshHeaderI18n();
    if (this.searchInput) this.searchInput.placeholder = t('grid.searchPlaceholder');
    this.updateTruncatedChip(this.activeView?.getVisibleCount() ?? 0); // re-word the truncation chip
    this.updateBulkActionBar();
    this.updateExpandCollapseIcon(); // re-translate the expand/collapse-all hint
    const addLabel = t('grid.addLabel');
    for (const row of this.pool) {
      if (row.currentSymbolId === null || row.currentIndex < 0) continue;
      const visible = this.activeView?.getSymbolAtIndex(row.currentIndex);
      if (!visible) continue;
      // renderVisibleRows() skips updateRow for rows whose index didn't change (a
      // language switch moves nothing), so force it: invalidate the type/perm cache
      // (sentinel ≠ any real value → typeChanged + permChanged → re-formats type,
      // access, value, and the priority tooltip) then call updateRow directly.
      row.cache.type = SENTINEL_DIRTY;
      row.cache.perm = SENTINEL_DIRTY;
      this.updateRow(row, visible, row.currentIndex);
      // 'add label' placeholders aren't a symbol/label change, so re-set directly.
      if (row.labelDisplay.classList.contains('vg-label-placeholder')) row.labelDisplay.textContent = addLabel;
    }
  }

  /** Chart config for a PRIORITY-polled symbol: the global config with the theme
   *  bumped one step in the rotation, so priority charts are always a colour ahead
   *  of the global theme. Memoised on the source theme (rebuilt only when it
   *  changes) so the hot per-frame draw path doesn't allocate. */
  private priorityCfgMemo?: { srcTheme: string; cfg: SparklineConfig };
  private priorityChartConfig(base: SparklineConfig): SparklineConfig {
    if (this.priorityCfgMemo?.srcTheme !== base.theme) {
      this.priorityCfgMemo = { srcTheme: base.theme, cfg: { ...base, theme: nextChartTheme(base.theme) } };
    }
    return this.priorityCfgMemo.cfg;
  }

  private buildHeaderRow(): HTMLDivElement {
    const header = document.createElement('div');
    header.className = 'vg-header';

    const columns: { id: keyof typeof COL_LABEL_KEYS; className: string }[] = [
      { id: 'select', className: 'vg-col-select' },
      { id: 'name', className: 'vg-col-name' },
      { id: 'label', className: 'vg-col-label' },
      { id: 'type', className: 'vg-col-type' },
      { id: 'access', className: 'vg-col-access' },
      { id: 'value', className: 'vg-col-value' },
      { id: 'chart', className: 'vg-col-chart' },
      { id: 'actions', className: 'vg-col-actions' },
    ];

    // Resizable columns (indices that support it — skip select)
    const resizableIds = new Set(['name', 'label', 'type', 'access', 'value', 'chart']);
    // Sortable columns — everything except chart, actions, select
    const sortableIds = new Set(['name', 'label', 'type', 'access', 'value']);

    for (const col of columns) {
      const cell = document.createElement('div');
      cell.className = `vg-header-cell ${col.className}`;
      cell.dataset.colId = col.id;

      if (col.id === 'select') {
        const cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.className = 'vg-select-all';
        cb.title = t(COL_LABEL_KEYS.select);
        cb.addEventListener('change', () => this.handleSelectAll(cb.checked));
        cell.appendChild(cb);
      } else {
        const span = document.createElement('span');
        span.className = 'vg-header-label';
        span.textContent = t(COL_LABEL_KEYS[col.id]);
        cell.appendChild(span);

        // Add sort arrow indicator for sortable columns
        if (sortableIds.has(col.id)) {
          const { svg: arrow } = createSortArrowSvg();
          cell.appendChild(arrow);
          this.sortArrows.set(col.id, arrow);
        }

        // Chart column header carries the per-chart theme picker (settings gear).
        // It's the reusable <vitesse-chart-theme-picker> (→ shared dropdown); we
        // stop mousedown like the resize handle so clicking it never starts a
        // column drag/sort.
        if (col.id === 'chart') {
          const picker = document.createElement('vitesse-chart-theme-picker');
          picker.className = 'vg-chart-settings';
          picker.addEventListener('mousedown', (e) => e.stopPropagation());
          cell.appendChild(picker);
        }
      }

      // Click handler for sortable columns
      if (sortableIds.has(col.id)) {
        cell.addEventListener('click', (e) => {
          // Don't sort if the click landed on a resize wall (the full-height grab
          // zone now overlaps the header cells) or the legacy thin handle.
          if ((e.target as HTMLElement).closest('.vg-resize-handle')) return;
          if (this.columnBoundaryAt(e.clientX, e.clientY) >= 0) return;
          this.handleHeaderSortClick(col.id);
        });
      }

      // Add resize handle to resizable columns
      if (resizableIds.has(col.id)) {
        const handle = document.createElement('div');
        handle.className = 'vg-resize-handle';
        handle.addEventListener('mousedown', (e) => {
          e.stopPropagation(); // Prevent drag reorder
          e.preventDefault();
          this.startColumnResize(e.clientX, Array.from(this.headerRow.children).indexOf(cell));
        });
        cell.appendChild(handle);
      }

      // Column drag-reorder removed by product decision — columns are fixed-order;
      // only width-resize (the wall grab) remains.

      header.appendChild(cell);
    }

    return header;
  }

  private createToolbarButton(className: string, title: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.className = `vg-toolbar-btn ${className}`;
    btn.title = title;
    return btn;
  }

  // ============================================================
  // Row Pool
  // ============================================================

  private buildRowPool(): void {
    for (let i = 0; i < POOL_SIZE; i++) {
      const row = this.createPooledRow(i);
      this.pool.push(row);
      this.scrollContainer.appendChild(row.element);
    }
  }

  private createPooledRow(poolIndex: number): PooledRow {
    const el = document.createElement('div');
    el.className = 'vg-row';
    el.style.transform = 'translateY(-9999px)';
    el.dataset.poolIndex = String(poolIndex);

    // --- Cells ---
    const selectCell = document.createElement('div');
    selectCell.className = 'vg-cell vg-col-select';
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'vg-row-checkbox';
    checkbox.tabIndex = -1;
    selectCell.appendChild(checkbox);

    const nameCell = document.createElement('div');
    nameCell.className = 'vg-cell vg-col-name';
    const { svg: chevronSvg, pathEl: chevronPath } = createChevronSvg();
    chevronSvg.style.visibility = 'hidden'; // matches cache.hasChildren = false
    chevronSvg.style.transform = 'rotate(-90deg)'; // matches cache.isExpanded = false
    const chevronWrap = document.createElement('span');
    chevronWrap.className = 'vg-chevron-wrap';
    chevronWrap.appendChild(chevronSvg);
    const nameSpan = document.createElement('span');
    nameSpan.className = 'vg-name-text';
    nameCell.appendChild(chevronWrap);
    nameCell.appendChild(nameSpan);

    const labelCell = document.createElement('div');
    labelCell.className = 'vg-cell vg-col-label';
    const labelDisplay = document.createElement('span');
    labelDisplay.className = 'vg-label-text';
    const labelInput = document.createElement('input');
    labelInput.className = 'vg-label-input';
    labelInput.type = 'text';
    labelInput.placeholder = t('grid.addLabel');
    labelInput.style.display = 'none';
    labelCell.appendChild(labelDisplay);
    labelCell.appendChild(labelInput);

    const typeCell = document.createElement('div');
    typeCell.className = 'vg-cell vg-col-type';
    const typeSpan = document.createElement('span');
    typeSpan.className = 'vg-type-badge';
    typeCell.appendChild(typeSpan);

    const accessCell = document.createElement('div');
    accessCell.className = 'vg-cell vg-col-access';
    const accessSpan = document.createElement('span');
    accessCell.appendChild(accessSpan);

    const valueCell = document.createElement('div');
    valueCell.className = 'vg-cell vg-col-value';
    const valueSpan = document.createElement('span');
    valueSpan.className = 'vg-value-text';
    valueCell.appendChild(valueSpan);
    // FRESHNESS. A value that stopped arriving looks identical to one arriving
    // every second — and on a push source that ambiguity is the normal case, not
    // an edge one: a broker topic that goes quiet is indistinguishable from one
    // that is simply slow. Health does not help, because nothing is unhealthy; the
    // connection is fine and the data merely stopped (that is also how the
    // frozen-leaf-registry defect presents — see docs/flattening.md §4).
    //
    // Deliberately an AGE, not a stale/fresh verdict: a threshold would need each
    // path's expected cadence, which we do not know, so an hourly-reporting sensor
    // would be permanently flagged. Showing the number and letting the operator
    // judge is the honest version.
    const valueAge = document.createElement('span');
    valueAge.className = 'vg-value-age';
    valueCell.appendChild(valueAge);
    // Square-pen value editor — ALWAYS visible on writable rows (even when the
    // value column is narrow: the text truncates behind it). Hidden on read-only.
    const valueEditBtn = document.createElement('button');
    valueEditBtn.className = 'vg-value-edit-btn';
    valueEditBtn.appendChild(createSquarePenSvg());
    valueEditBtn.style.display = 'none'; // updateRow reveals it for writable leaves
    valueCell.appendChild(valueEditBtn);

    const chartCell = document.createElement('div');
    chartCell.className = 'vg-cell vg-col-chart';
    const sparklineCanvas = document.createElement('canvas');
    sparklineCanvas.className = 'vg-sparkline';
    sparklineCanvas.style.display = 'none';
    chartCell.appendChild(sparklineCanvas);

    const actionsCell = document.createElement('div');
    actionsCell.className = 'vg-cell vg-col-actions';

    // Priority-polling "!" toggle (replaces the old subscribe bell — everything
    // is always subscribed now). Red when priority is set; see updateRow.
    const alertIcon = createAlertSvg();
    const subscribeButton = document.createElement('button');
    subscribeButton.className = 'vg-action-btn vg-priority-btn';
    subscribeButton.appendChild(alertIcon);

    // Write-lock padlock toggle (open = unlocked, closed = locked). Blue when
    // locked; see updateRow. Refusing writes to a locked symbol is enforced by
    // the plugin (the sole authority).
    const lock = createLockSvg();
    const lockButton = document.createElement('button');
    lockButton.className = 'vg-action-btn vg-lock-btn';
    lockButton.appendChild(lock.svg);

    // Delete (trash) — only for plugin-deletable leaves (typically manually-added
    // symbols); updateRow reveals it. Red on hover (the destructive treatment).
    const deleteButton = document.createElement('button');
    deleteButton.className = 'vg-action-btn vg-delete-btn';
    deleteButton.appendChild(createTrashSvg());
    deleteButton.style.display = 'none';

    actionsCell.appendChild(subscribeButton);
    actionsCell.appendChild(lockButton);
    actionsCell.appendChild(deleteButton);

    // Assemble row
    el.appendChild(selectCell);
    el.appendChild(nameCell);
    el.appendChild(labelCell);
    el.appendChild(typeCell);
    el.appendChild(accessCell);
    el.appendChild(valueCell);
    el.appendChild(chartCell);
    el.appendChild(actionsCell);

    return {
      element: el,
      cells: {
        select: selectCell,
        name: nameCell,
        label: labelCell,
        type: typeCell,
        access: accessCell,
        value: valueCell,
        chart: chartCell,
        actions: actionsCell,
      },
      checkbox,
      chevronSvg,
      chevronPath,
      nameSpan,
      labelDisplay,
      labelInput,
      typeSpan,
      accessSpan,
      valueSpan,
      valueAge,
      valueEditBtn,
      subscribeButton,
      subscribeSvg: alertIcon,
      lockButton,
      lockSetState: lock.setLocked,
      deleteButton,
      sparklineCanvas,
      currentSymbolId: null,
      currentIndex: -1,
      cache: {
        symbolId: '',
        level: -1,
        hasChildren: false,
        isExpanded: false,
        priority: false,
        locked: false,
        connected: false,
        type: '',
        perm: '',
        label: undefined,
        selected: false,
        chartMinMaxVersion: -1,
        chartLastDrawTime: 0,
        lastValue: undefined,
        lastFormattedValue: '',
      },
    };
  }

  // ============================================================
  // Virtual Scrolling
  // ============================================================

  /** Find the nearest scrollable ancestor */
  private findScrollParent(): HTMLElement {
    // Validate cached ref is still in the DOM
    if (this.scrollParent && this.scrollParent.isConnected) return this.scrollParent;
    this.scrollParent = null;
    // Walk up from our container to find the actual scrollable element
    let el: HTMLElement | null = this.container;
    while (el) {
      if (el.classList.contains('main-content')) {
        this.scrollParent = el;
        return el;
      }
      const style = getComputedStyle(el);
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
        this.scrollParent = el;
        return el;
      }
      el = el.parentElement;
    }
    // Fallback: use documentElement (shouldn't happen in our layout)
    this.scrollParent = document.documentElement;
    return document.documentElement;
  }

  /** Compute offset of scroll container relative to the scroll parent */
  private getScrollMargin(): number {
    const scrollEl = this.findScrollParent();
    const scrollRect = scrollEl.getBoundingClientRect();
    const containerRect = this.scrollContainer.getBoundingClientRect();
    return containerRect.top - scrollRect.top + scrollEl.scrollTop;
  }

  private setupVirtualizer(): void {
    const count = this.activeView?.getVisibleCount() || 0;
    const scrollEl = this.findScrollParent();
    const scrollMargin = this.getScrollMargin();

    // Update scroll container height
    this.scrollContainer.style.height = `${count * ROW_HEIGHT}px`;

    if (this.virtualizer) {
      // Update count and scroll margin (may have changed if page layout shifted)
      this.virtualizer.setOptions({
        ...this.virtualizer.options,
        count,
        scrollMargin,
      });
      // Notify virtualizer of option changes
      this.virtualizer._willUpdate();
      return;
    }

    this.virtualizer = new Virtualizer({
      count,
      getScrollElement: () => scrollEl,
      estimateSize: () => ROW_HEIGHT,
      overscan: OVERSCAN_ROWS,
      observeElementOffset: observeElementOffset,
      observeElementRect: observeElementRect,
      scrollToFn: elementScroll,
      scrollMargin,
      onChange: () => {
        if (this.animating) return;
        if (this.rafId) cancelAnimationFrame(this.rafId);
        this.rafId = requestAnimationFrame(() => this.renderVisibleRows());
      },
    });

    // Must call _didMount and _willUpdate to start scroll observation.
    this.virtualizerCleanup = this.virtualizer._didMount();
    this.virtualizer._willUpdate();
  }

  // ============================================================
  // Rendering
  // ============================================================

  // Reusable scratch for renderVisibleRows (runs every scroll frame). The pool is
  // fixed-size and these never escape the method, so reusing + clearing them each
  // call avoids 4 collection allocations per frame. renderVisibleRows is NOT
  // re-entrant (synchronous, driven by a single virtualizer onChange), so reuse
  // is safe.
  private readonly _rvRowBySymbol = new Map<string, PooledRow>();
  private readonly _rvClaimed = new Set<PooledRow>();
  private readonly _rvUnmatched: number[] = [];
  private readonly _rvFree: PooledRow[] = [];

  private renderVisibleRows(): void {
    if (this.animating) return;
    if (!this.activeView || !this.virtualizer) {
      // Hide all rows
      for (const row of this.pool) {
        row.element.style.transform = 'translateY(-9999px)';
        row.currentSymbolId = null;
        row.currentIndex = -1;
      }
      return;
    }

    const items = this.virtualizer.getVirtualItems();
    // Report the on-screen index range to a view that viewport-scopes its value
    // lease (integrations) — this drives the per-viewport value follow so the
    // backend produces only what's visible.
    if (items.length > 0) {
      (this.activeView as { setVisibleRange?: (s: number, e: number) => void }).setVisibleRange?.(
        items[0].index,
        items[items.length - 1].index,
      );
    }
    const scrollMargin = this.virtualizer.options.scrollMargin;

    // Build lookup: symbolId → pool row (for rows that currently show a symbol).
    // Reused scratch (fields above) — cleared, then refilled, each call.
    const rowBySymbol = this._rvRowBySymbol;
    rowBySymbol.clear();
    for (const row of this.pool) {
      if (row.currentSymbolId) rowBySymbol.set(row.currentSymbolId, row);
    }

    // Track which pool rows are claimed by virtual items
    const claimed = this._rvClaimed;
    claimed.clear();
    // Track which vItems still need a pool row
    const unmatched = this._rvUnmatched;
    unmatched.length = 0;

    // Pass 1: Match vItems to pool rows that already show the correct symbol.
    for (let i = 0; i < items.length; i++) {
      const vItem = items[i];
      const visible = this.activeView.getSymbolAtIndex(vItem.index);
      if (!visible) continue;

      const existing = rowBySymbol.get(visible.symbolId);
      if (existing && !claimed.has(existing)) {
        claimed.add(existing);
        existing.element.style.transform = `translateY(${vItem.start - scrollMargin}px)`;
        // Only call updateRow if index changed (expand state, etc. may differ)
        if (existing.currentIndex !== vItem.index) {
          this.updateRow(existing, visible, vItem.index);
        }
        existing.currentIndex = vItem.index;
        existing.currentSymbolId = visible.symbolId;
      } else {
        unmatched.push(i);
      }
    }

    // Collect unclaimed pool rows (available for reassignment) — reused scratch.
    const free = this._rvFree;
    free.length = 0;
    for (const row of this.pool) {
      if (!claimed.has(row)) free.push(row);
    }

    // Pass 2: Assign free pool rows to unmatched vItems
    let fi = 0;
    for (const itemIdx of unmatched) {
      const vItem = items[itemIdx];
      const visible = this.activeView.getSymbolAtIndex(vItem.index);
      if (!visible || fi >= free.length) continue;

      const row = free[fi++];
      row.element.style.transform = `translateY(${vItem.start - scrollMargin}px)`;
      this.updateRow(row, visible, vItem.index);
      row.currentIndex = vItem.index;
      row.currentSymbolId = visible.symbolId;
    }

    // Hide leftover pool rows
    for (; fi < free.length; fi++) {
      const row = free[fi];
      row.element.style.transform = 'translateY(-9999px)';
      row.currentSymbolId = null;
      row.currentIndex = -1;
    }
  }

  /**
   * Update a pooled row's DOM to display a given symbol.
   */
  private updateRow(
    row: PooledRow,
    visible: { symbolId: string; nodeId: string; level: number; hasChildren: boolean; isExpanded: boolean },
    index: number,
  ): void {
    const { symbolId, nodeId, level, hasChildren, isExpanded } = visible;

    // If this row is being edited and is about to show a different symbol, commit first
    if (this.editingRow?.poolRow === row && this.editingRow.symbolId !== symbolId) {
      this.commitLabelEdit();
    }

    // Read live data from the source (O(1) each)
    const sym = this.source.getSymbol(symbolId);
    const value = this.source.getValue(symbolId);
    const label = this.source.getLabel(symbolId);
    // Everything is always subscribed (no subscribe notion); the action button is
    // the priority "!" toggle.
    const priority = this.source.getPriority ? this.source.getPriority(symbolId) : false;
    const locked = this.source.getLocked ? this.source.getLocked(symbolId) : false;
    const connected = this.isConnected;
    const type = sym?.type || '';
    const perm = sym?.permission || 'r';
    const selected = this.isSelected(symbolId);

    const c = row.cache;

    // Snapshot old cache values BEFORE any mutations for correct dirty checks
    const symChanged = c.symbolId !== symbolId;
    const levelChanged = c.level !== level;
    const childrenChanged = c.hasChildren !== hasChildren;
    const expandChanged = c.isExpanded !== isExpanded;
    const labelChanged = c.label !== label;
    const typeChanged = c.type !== type;
    const permChanged = c.perm !== perm;
    const connChanged = c.connected !== connected;
    const priorityChanged = c.priority !== priority;
    const lockedChanged = c.locked !== locked;
    const selChanged = c.selected !== selected;

    // Row data attributes — always set index (changes on scroll)
    row.element.dataset.index = String(index);

    // --- Skip DOM writes for unchanged fields (dirty-flag pattern) ---

    if (symChanged) {
      row.element.dataset.symbolId = symbolId;
      row.element.dataset.nodeId = nodeId;
      row.nameSpan.textContent = symbolId;
      row.nameSpan.title = symbolId;
    }

    if (selChanged) {
      row.element.classList.toggle('vg-row-selected', selected);
      row.checkbox.checked = selected;
    }

    // --- Name cell ---
    if (levelChanged) {
      row.cells.name.style.paddingLeft = `${level * 20 + 12}px`;
    }
    if (childrenChanged) {
      row.chevronSvg.style.visibility = hasChildren ? 'visible' : 'hidden';
    }
    if (expandChanged || childrenChanged) {
      // Suppress CSS transition when row is recycled to a different symbol (scroll);
      // only animate when the same symbol's expand state changes (user toggle).
      if (symChanged) {
        row.chevronSvg.style.transition = 'none';
        row.chevronSvg.style.transform = isExpanded ? '' : 'rotate(-90deg)';
        // Re-enable transition next frame so future toggles animate
        requestAnimationFrame(() => {
          row.chevronSvg.style.transition = '';
        });
      } else {
        row.chevronSvg.style.transform = isExpanded ? '' : 'rotate(-90deg)';
      }
    }

    // --- Label cell ---
    if (this.editingRow?.poolRow !== row) {
      if (symChanged || labelChanged) {
        row.labelInput.style.display = 'none';
        row.labelDisplay.style.display = '';
        if (label) {
          row.labelDisplay.textContent = label;
          row.labelDisplay.className = 'vg-label-text';
        } else {
          row.labelDisplay.textContent = t('grid.addLabel');
          row.labelDisplay.className = 'vg-label-text vg-label-placeholder';
        }
      }
    }

    // --- Type ---
    if (typeChanged) {
      row.typeSpan.textContent = formatType(type);
      row.typeSpan.className = 'vg-type-badge';
    }

    // --- Access ---
    if (permChanged) {
      row.accessSpan.textContent = formatPermission(perm);
    }

    // --- Freshness --- (see createRow; refreshed independently by refreshAges)
    this.paintAge(row, symbolId, type);

    // --- Value --- (dirty-check raw value before formatting)
    if (value !== c.lastValue || symChanged || typeChanged) {
      let fmtValue: string;
      if (value !== null && value !== undefined) {
        fmtValue = formatValue(value, type, sym?.enumValues);
        // Cache for disconnect preservation (bounded — see setStaleValue).
        this.setStaleValue(symbolId, fmtValue);
      } else if (!connected) {
        // Disconnected + no value — show last known value from stale cache
        fmtValue = this.staleValues.get(symbolId) || formatValue(value, type, sym?.enumValues);
      } else {
        fmtValue = formatValue(value, type, sym?.enumValues);
      }
      if (fmtValue !== c.lastFormattedValue) {
        row.valueSpan.textContent = fmtValue;
        row.valueSpan.title = fmtValue;
        c.lastFormattedValue = fmtValue;
      }
      c.lastValue = value;
    }

    if (connChanged) {
      row.valueSpan.classList.toggle('vg-stale', !connected);
    }

    // Read-only sources (integrations: observe-while-watched) have NO per-row
    // priority (no PLC-PDU polling concept) and NO write-lock (writes are
    // model-authored, not from this grid) — hide both. Labels still apply.
    const writeActionsHidden = (this.source as { readOnlyActions?: boolean }).readOnlyActions === true;
    // What the USER may do, as distinct from what the SOURCE supports above. Read
    // once per row update — plain booleans, computed when the session changes.
    const may = (this.source as { may?: GridPermissions }).may;

    // --- Priority "!" button ---
    if (priorityChanged || permChanged || connChanged || childrenChanged) {
      // Containers have no value to poll; hide the toggle for them (and read-only sources).
      // Priority is bought (telemetry), not granted — so an org without it simply
      // has no button, the same as a source with no polling concept.
      row.subscribeButton.style.visibility =
        hasChildren || writeActionsHidden || may?.priority === false ? 'hidden' : 'visible';
      row.subscribeButton.disabled = !connected;
      // Blue when active via the .vg-priority-active class (no inline style); the
      // CHART is what shifts +1 theme — see updateRowChart.
      row.subscribeButton.classList.toggle('vg-priority-active', priority);
      row.subscribeButton.title = priority ? t('grid.priorityOn') : t('grid.priorityOff');
    }

    // --- Lock padlock button ---
    // Offered only where a lock can actually protect something. A write-lock
    // exists to REFUSE A WRITE, so on a symbol nothing can write it guarantees
    // nothing — a container (no write target) or a read-only symbol. Showing it
    // there is worse than showing nothing: the plugin refuses the request, and
    // until it answers the row reads as protected. `perm` is the symbol's own
    // permission, so this holds for every plugin without the UI knowing any
    // protocol's type rules; a plugin may still refuse for reasons only it knows
    // (see indexerPagedView.setLocked, which reverts and says why).
    //
    // permChanged is included so a language switch (refreshI18n invalidates perm)
    // re-translates the title, matching the priority block.
    if (lockedChanged || permChanged || childrenChanged || connChanged) {
      const lockable = !hasChildren && perm.includes('w');
      row.lockButton.style.visibility =
        !lockable || writeActionsHidden || may?.lock === false ? 'hidden' : 'visible';
      row.lockButton.disabled = !connected;
      row.lockSetState(locked); // swap open/closed shackle
      // Blue when locked (.vg-lock-active); see symbolGrid.scss.
      row.lockButton.classList.toggle('vg-lock-active', locked);
      row.lockButton.title = locked ? t('grid.lockOn') : t('grid.lockOff');
    }

    // --- Value-edit square-pen --- value-bearing writable rows only; always shown
    // (the value text truncates behind it) so it stays findable even in a narrow
    // column. typeChanged is in the guard because the decision now reads `type`:
    // without it a pooled row recycled between two symbols with the same permission
    // and child count would keep the previous symbol's pencil state.
    if (permChanged || childrenChanged || connChanged || typeChanged) {
      // `perm` is what the SYMBOL allows; `may.write` is what the USER may do. A
      // read-only symbol and a user without org:symbol:write both mean no pencil.
      //
      // Gated on the row's TYPE, not on whether it has children. A broker topic row
      // carries a whole message AND may have rows nested under it — its children are
      // an accident of which characters its neighbours' names contain, and must not
      // decide whether the message it holds can be published. A structural container
      // still gets nothing: carriesValue is false for object/array. See rowValue.ts.
      //
      // NOT gated on whether the row has reported a value. A row with no value yet
      // is the normal state of a quiet topic on a broker with no retained message
      // (NATS, NSQ, Pulsar, RabbitMQ), and a hand-named subject such as
      // `commands.line1.start` exists precisely so a person can publish the first
      // message to it. The editor opens with an empty box in that case (see
      // openValueEditor) and refuses to save blank text, so nothing is published
      // that the user did not type. This is the browser's own convenience only: the
      // write RPC is the same one SCXML models call, and it never required a prior
      // value.
      const writable = carriesValue(type) && perm.includes('w') && may?.write !== false;
      row.valueEditBtn.style.display = writable ? '' : 'none';
      row.valueEditBtn.disabled = !connected;
      row.valueEditBtn.title = t('grid.editValue');
    }

    // --- Delete (trash) --- only for plugin-deletable LEAVES (manually-added).
    if (symChanged || childrenChanged || connChanged) {
      const deletable = !hasChildren && (this.source.getDeletable?.(symbolId) ?? false) && may?.config !== false;
      row.deleteButton.style.display = deletable ? '' : 'none';
      row.deleteButton.disabled = !connected;
      row.deleteButton.title = 'Delete symbol';
    }

    // --- Update cache in one pass at the end ---
    // Reset chart cache when symbol changes so updateRowChart always redraws
    if (symChanged) {
      c.chartMinMaxVersion = -1;
      c.chartLastDrawTime = 0;
      c.lastValue = undefined;
      c.lastFormattedValue = '';
    }
    c.symbolId = symbolId;
    c.level = level;
    c.hasChildren = hasChildren;
    c.isExpanded = isExpanded;
    c.label = label;
    c.type = type;
    c.perm = perm;
    c.connected = connected;
    c.priority = priority;
    c.locked = locked;
    c.selected = selected;

    // --- Chart --- (everything is subscribed → every chartable symbol charts)
    this.updateRowChart(row, symbolId, type);
  }

  private updateRowChart(row: PooledRow, symbolId: string, type: string, config?: SparklineConfig): void {
    // Everything is subscribed; a symbol charts iff its type is numeric/boolean.
    if (type !== 'number' && type !== 'integer' && type !== 'boolean' && type !== 'bool') {
      if (row.sparklineCanvas.style.display !== 'none') {
        row.sparklineCanvas.style.display = 'none';
        row.cache.chartMinMaxVersion = -1;
      }
      return;
    }

    if (row.sparklineCanvas.style.display === 'none') {
      row.sparklineCanvas.style.display = '';
    }

    const raw = this.source.getSymbolBufferRaw(symbolId);

    if (raw && raw.count > 0) {
      const now = performance.now();
      const dataChanged = raw.minMaxVersion !== row.cache.chartMinMaxVersion || row.cache.symbolId !== symbolId;
      // New data → always redraw; same data → still redraw at ~60 Hz so the
      // time axis FLOWS left every frame (the scroll is what must be smooth;
      // redrawing only on new data made it step at the data rate). Visible
      // rows only, ≤115-point canvas each — cheap.
      if (!dataChanged && now - row.cache.chartLastDrawTime < 15) {
        return;
      }
      // Priority-polled symbols chart one theme ahead of the global theme; normal
      // symbols stay on the global theme.
      const base = config || this.getChartConfig();
      const isPriority = this.source.getPriority ? this.source.getPriority(symbolId) : false;
      const cfg = isPriority ? this.priorityChartConfig(base) : base;
      drawSparkline(row.sparklineCanvas, raw.timestamps, raw.values, raw.count, cfg, raw.head, raw.capacity, raw.minMaxVersion, false, raw.nowMs, raw.scaleMin, raw.scaleMax);
      row.cache.chartMinMaxVersion = raw.minMaxVersion;
      row.cache.chartLastDrawTime = now;

      // Re-draw tooltip overlay if this canvas is being hovered (same +1 theme)
      if (this.hoveredCanvas === row.sparklineCanvas && this.hoverState) {
        this.renderHoverOverlay(row.sparklineCanvas, cfg);
      }
    } else {
      if (row.cache.chartMinMaxVersion !== -1) {
        clearSparkline(row.sparklineCanvas);
        row.cache.chartMinMaxVersion = -1;
      }
    }
  }

  // ============================================================
  // Event Delegation
  // ============================================================

  private setupEventDelegation(): void {
    this.scrollContainer.addEventListener('click', (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const rowEl = target.closest('.vg-row') as HTMLElement;
      if (!rowEl) return;

      const symbolId = rowEl.dataset.symbolId;
      const nodeId = rowEl.dataset.nodeId;
      if (!symbolId || !nodeId) return;

      // Chevron click → expand/collapse (blocked during animation)
      if (target.closest('.vg-chevron-wrap')) {
        if (this.animating) return;
        this.activeView?.toggleExpand(nodeId);
        return;
      }

      // Checkbox
      if (target.closest('.vg-row-checkbox')) {
        this.toggleSelection(symbolId);
        return;
      }

      // Priority "!" toggle
      if (target.closest('.vg-priority-btn')) {
        e.stopPropagation();
        const on = this.source.getPriority ? this.source.getPriority(symbolId) : false;
        this.source.setPriority?.(symbolId, !on);
        // Re-render this row's button immediately (optimistic).
        const r = this.pool.find((p) => p.currentSymbolId === symbolId);
        if (r) this.updateRow(r, this.activeView!.getSymbolAtIndex(r.currentIndex)!, r.currentIndex);
        return;
      }

      // Lock padlock toggle
      if (target.closest('.vg-lock-btn')) {
        e.stopPropagation();
        const isLocked = this.source.getLocked ? this.source.getLocked(symbolId) : false;
        this.source.setLocked?.(symbolId, !isLocked);
        const r = this.pool.find((p) => p.currentSymbolId === symbolId);
        if (r) this.updateRow(r, this.activeView!.getSymbolAtIndex(r.currentIndex)!, r.currentIndex);
        return;
      }

      // Delete (trash) → remove a plugin-deletable symbol. The plugin removes it
      // and won't re-discover it → the indexer drops it → the row vanishes on the
      // next window fetch (the grid's structural-change refresh handles removal).
      if (target.closest('.vg-delete-btn')) {
        e.stopPropagation();
        if (!this.isConnected) return;
        void this.source.deleteSymbol?.(symbolId);
        return;
      }

      // Label cell click → start editing
      if (target.closest('.vg-col-label')) {
        if (!this.isConnected) return;
        const poolRow = this.findPoolRowByElement(rowEl);
        if (poolRow) this.startLabelEdit(poolRow, symbolId);
        return;
      }

      // Square-pen → open the value editor (precedence over the view handler below).
      if (target.closest('.vg-value-edit-btn')) {
        e.stopPropagation();
        if (!this.isConnected) return;
        const poolRow = this.findPoolRowByElement(rowEl);
        if (poolRow) this.openValueEditor(poolRow);
        return;
      }

      // Value cell click → view value
      if (target.closest('.vg-col-value')) {
        this.emit('viewValue', { controllerId: this.currentControllerId, symbolId });
        return;
      }
    });

    // Column-wall grab + sparkline tooltip, unified over the WHOLE grid (the
    // header titles row + the data rows) so a wall highlights blue + is grabbable
    // along its ENTIRE height, not just the data area. Attached to the wrapper —
    // ONE region with no header↔rows seam (which would flicker the highlight when
    // crossing it); columnBoundaryAt gates to header.top..rows.bottom so the
    // toolbar above the header never reports a wall. The wall affordance takes
    // precedence: on a boundary we show col-resize + the highlight and suppress
    // the chart tooltip (sparklines live only in the rows, so hit-test there).
    this.wrapper.addEventListener('mousemove', (e: MouseEvent) => {
      this.lastPointer = { x: e.clientX, y: e.clientY };
      const boundary = this.columnBoundaryAt(e.clientX, e.clientY);
      this.setColumnHoverBoundary(boundary);
      if (boundary >= 0) {
        this.hideSparklineTooltip();
        return;
      }
      const target = e.target as HTMLElement;
      const hit = this.scrollContainer.contains(target) ? this.hitTestSparklineAt(e.clientX, e.clientY, target) : null;
      if (!hit) {
        this.hideSparklineTooltip();
        return;
      }
      this.showSparklineTooltip(e.clientX, e.clientY, hit.canvas, hit.symbolId);
    });

    // Grab a column wall at any height — header OR rows → start the resize drag.
    this.wrapper.addEventListener('mousedown', (e: MouseEvent) => {
      const boundary = this.columnBoundaryAt(e.clientX, e.clientY);
      if (boundary >= 0) {
        e.preventDefault(); // don't text-select / start a row or header-sort interaction
        this.startColumnResize(e.clientX, boundary);
      }
    });

    this.wrapper.addEventListener('mouseleave', () => {
      this.hideSparklineTooltip();
      this.setColumnHoverBoundary(-1);
    });

    // Label input events
    this.scrollContainer.addEventListener('keydown', (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (!target.classList.contains('vg-label-input')) return;

      if (e.key === 'Enter') {
        e.preventDefault();
        const fromIndex = this.editingRow?.poolRow.currentIndex ?? -1;
        this.commitLabelEdit();
        if (fromIndex >= 0) this.moveLabelFocus(fromIndex, 1);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        const fromIndex = this.editingRow?.poolRow.currentIndex ?? -1;
        this.cancelLabelEdit();
        if (fromIndex >= 0) this.moveLabelFocus(fromIndex, 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const fromIndex = this.editingRow?.poolRow.currentIndex ?? -1;
        this.cancelLabelEdit();
        if (fromIndex >= 0) this.moveLabelFocus(fromIndex, -1);
      } else if (e.key === 'Escape') {
        this.cancelLabelEdit();
      }
    });

    this.scrollContainer.addEventListener('focusout', (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('vg-label-input') && this.editingRow) {
        // Blur without Enter → revert, don't save
        // But skip if we're navigating to another label (moveLabelFocus sets this flag)
        if (!this._labelNavInProgress) {
          this.cancelLabelEdit();
        }
      }
    });

    // Search keyboard events
    this.searchInput.addEventListener('keydown', (e: KeyboardEvent) => {
      if (this.searchAutocomplete?.isOpen()) {
        if (['ArrowDown', 'ArrowUp', 'Tab', 'Enter', 'Escape'].includes(e.key)) {
          this.searchAutocomplete.handleKeyDown(e);
          return;
        }
      }
    });

    // Global keyboard shortcut: Cmd+/ (Mac) or Ctrl+/ (Windows/Linux) focuses symbol search
    this.keyboardShortcutHandler = (e: KeyboardEvent) => {
      if (e.key === '/' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        this.searchInput.focus();
      }
    };
    document.addEventListener('keydown', this.keyboardShortcutHandler);
  }

  /** Emit events for external handling (subscribe toggle, value view). */
  private emit(event: string, data: unknown): void {
    this.container.dispatchEvent(new CustomEvent(`symbolGrid:${event}`, { detail: data, bubbles: true }));
  }

  // ============================================================
  // Selection
  // ============================================================

  private isSelected(symbolId: string): boolean {
    if (this.selectAllActive) return !this.deselectedSymbols.has(symbolId);
    return this.selectedSymbols.has(symbolId);
  }

  private getSelectedCount(): number {
    if (this.selectAllActive) {
      const totalVisible = this.activeView?.getVisibleCount() ?? 0;
      return totalVisible - this.deselectedSymbols.size;
    }
    return this.selectedSymbols.size;
  }

  private clearSelection(): void {
    this.selectedSymbols.clear();
    this.selectAllActive = false;
    this.deselectedSymbols.clear();
  }

  private toggleSelection(symbolId: string): void {
    if (this.selectAllActive) {
      // In select-all mode, toggle via deselection set
      if (this.deselectedSymbols.has(symbolId)) {
        this.deselectedSymbols.delete(symbolId);
      } else {
        this.deselectedSymbols.add(symbolId);
      }
      // If all deselected, exit select-all mode
      const totalVisible = this.activeView?.getVisibleCount() ?? 0;
      if (this.deselectedSymbols.size >= totalVisible) {
        this.selectAllActive = false;
        this.deselectedSymbols.clear();
      }
    } else {
      if (this.selectedSymbols.has(symbolId)) {
        this.selectedSymbols.delete(symbolId);
      } else {
        this.selectedSymbols.add(symbolId);
      }
    }

    const isSelected = this.isSelected(symbolId);

    // Update the row's checkbox and selection class
    for (const row of this.pool) {
      if (row.currentSymbolId === symbolId) {
        row.checkbox.checked = isSelected;
        row.element.classList.toggle('vg-row-selected', isSelected);
        break;
      }
    }

    this.updateBulkActionBar();
    this.saveSelectionDebounced();
  }

  private handleSelectAll(checked: boolean): void {
    if (!this.activeView) return;

    if (checked) {
      // If already all selected, toggle to deselect all
      if (this.selectAllActive && this.deselectedSymbols.size === 0) {
        this.clearSelection();
        const selectAllCb = this.headerRow.querySelector('.vg-select-all') as HTMLInputElement;
        if (selectAllCb) selectAllCb.checked = false;
      } else {
        // Enter select-all mode: O(1) — no tree walk needed
        this.selectAllActive = true;
        this.deselectedSymbols.clear();
        this.selectedSymbols.clear(); // Clear individual selections
      }
    } else {
      this.clearSelection();
    }

    // Force re-render to update selection classes
    for (const row of this.pool) {
      row.currentIndex = -1;
    }
    this.renderVisibleRows();
    this.updateBulkActionBar();
    this.saveSelectionDebounced();
  }

  // ============================================================
  // Label Editing
  // ============================================================

  private startLabelEdit(row: PooledRow, symbolId: string): void {
    // Commit any existing edit
    if (this.editingRow) this.commitLabelEdit();

    const label = this.source.getLabel(symbolId) || '';
    row.labelDisplay.style.display = 'none';
    row.labelInput.style.display = '';
    row.labelInput.value = label;
    row.labelInput.focus();
    row.labelInput.select();

    this.editingRow = { poolRow: row, symbolId };
  }

  private commitLabelEdit(): void {
    if (!this.editingRow) return;
    const { poolRow, symbolId } = this.editingRow;
    const newLabel = poolRow.labelInput.value.trim() || undefined;

    poolRow.labelInput.style.display = 'none';
    poolRow.labelDisplay.style.display = '';
    this.editingRow = null;

    // Route to the source's optional label write (stub stores locally). The
    // real backend decides whether this is a UserData write or a no-op.
    this.source.setLabel?.(symbolId, newLabel);
  }

  private cancelLabelEdit(): void {
    if (!this.editingRow) return;
    const { poolRow } = this.editingRow;
    poolRow.labelInput.style.display = 'none';
    poolRow.labelDisplay.style.display = '';
    this.editingRow = null;
  }

  /**
   * Move label editing focus to an adjacent row.
   */
  private moveLabelFocus(fromIndex: number, direction: 1 | -1): void {
    if (!this.activeView || !this.virtualizer) return;

    const totalCount = this.activeView.getVisibleCount();
    if (totalCount === 0) return;

    // Cyclical wrap
    let targetIndex = fromIndex + direction;
    if (targetIndex >= totalCount) targetIndex = 0;
    if (targetIndex < 0) targetIndex = totalCount - 1;

    // Try to find the pool row already rendered at this index
    const existingRow = this.pool.find((r) => r.currentIndex === targetIndex);
    if (existingRow && existingRow.currentSymbolId) {
      this._labelNavInProgress = true;
      this.startLabelEdit(existingRow, existingRow.currentSymbolId);
      this._labelNavInProgress = false;
      return;
    }

    // Row not in pool — scroll to it, then start editing after render
    this._labelNavInProgress = true;
    this._labelNavTargetIndex = targetIndex;
    this.virtualizer.scrollToIndex(targetIndex, { align: 'auto' });

    // After scrollToIndex, the virtualizer fires onChange → renderVisibleRows in a rAF.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const row = this.pool.find((r) => r.currentIndex === this._labelNavTargetIndex);
        if (row && row.currentSymbolId) {
          this.startLabelEdit(row, row.currentSymbolId);
        }
        this._labelNavInProgress = false;
        this._labelNavTargetIndex = -1;
      });
    });
  }

  // ============================================================
  // View Change Handler
  // ============================================================

  private onViewChanged = (event: ViewChangedEvent): void => {
    if (!this.virtualizer || !this.activeView) return;
    if (this.animating) return; // Block new events during animation (rapid toggle)

    const newCount = event.visibleCount;
    // Hide the expand/collapse-all control only when the LOADED view is genuinely
    // empty. Driven by real view updates (not the transient count=0 on mount), so
    // it no longer flickers away while a controller is still loading.
    if (this.expandCollapseBtn) {
      this.expandCollapseBtn.style.display = this.toolbarCfg.bulkExpand !== false && newCount > 0 ? '' : 'none';
    }
    this.updateTruncatedChip(newCount);
    const newHeight = newCount * ROW_HEIGHT;
    const isSplice = event.type === 'splice' && event.spliceIndex !== undefined;
    const isExpand = isSplice && (event.insertCount || 0) > 0 && (event.removeCount || 0) === 0;
    const isCollapse = isSplice && (event.removeCount || 0) > 0 && (event.insertCount || 0) === 0;
    const spliceIndex = event.spliceIndex ?? 0;
    const insertCount = event.insertCount || 0;
    const removeCount = event.removeCount || 0;
    const deltaRows = insertCount - removeCount;
    const deltaHeight = deltaRows * ROW_HEIGHT;

    // Determine if this is an animated expand/collapse BEFORE updating virtualizer.
    const shouldAnimate = (isExpand && insertCount > 0) || (isCollapse && removeCount > 0);

    // Set animating flag BEFORE setOptions so the onChange guard blocks the rAF render
    if (shouldAnimate) {
      this.animating = true;
    }

    // Update virtualizer count
    this.virtualizer.setOptions({
      ...this.virtualizer.options,
      count: newCount,
    });
    this.virtualizer._willUpdate();

    // Adjust scroll if splice is above the viewport (skip animation)
    if (isSplice && deltaHeight !== 0) {
      const scrollEl = this.findScrollParent();
      const viewportStart = Math.floor((scrollEl.scrollTop - this.scrollContainer.offsetTop) / ROW_HEIGHT);
      if (spliceIndex < viewportStart) {
        this.animating = false;
        scrollEl.scrollTop += deltaHeight;
        this.updateEmptyState();
        for (const row of this.pool) row.currentIndex = -1;
        this.renderVisibleRows();
        this.scrollContainer.style.height = `${newHeight}px`;
        return;
      }
    }

    // Animate if expand or collapse with visible rows
    if (isExpand && insertCount > 0) {
      this.animateExpand(spliceIndex - 1, insertCount, newCount);
      return;
    }
    if (isCollapse && removeCount > 0) {
      this.animateCollapse(spliceIndex - 1, removeCount, newCount);
      return;
    }

    // Non-animated: full rebuild (filter change, setExpandAll, etc.)
    this.animating = false;
    this.updateEmptyState();
    for (const row of this.pool) row.currentIndex = -1;
    this.renderVisibleRows();
    this.scrollContainer.style.height = `${newHeight}px`;
  };

  // ============================================================
  // Expand/Collapse Animation
  // ============================================================

  private animateExpand(parentIndex: number, insertCount: number, newCount: number): void {
    const spliceIndex = parentIndex + 1;
    const newHeight = newCount * ROW_HEIGHT;
    const transition = `transform ${EXPAND_MS}ms ${ANIM_EASING}`;

    // 1. Animate container height (bottom border tracks rows)
    this.scrollContainer.style.transition = `height ${EXPAND_MS}ms ${ANIM_EASING}`;
    this.scrollContainer.style.height = `${newHeight}px`;

    // 2. Elevate parent — children emerge from behind it
    const parentRow = this.pool.find((r) => r.currentIndex === parentIndex);
    if (parentRow) {
      parentRow.element.style.zIndex = '2';
      parentRow.element.classList.add('vg-anim-opaque');
      // Update chevron to expanded state
      const visible = this.activeView!.getSymbolAtIndex(parentIndex);
      if (visible) this.updateRow(parentRow, visible, parentIndex);
    }

    // 3. Slide existing below-parent rows DOWN to make room
    for (const row of this.pool) {
      if (row.currentIndex >= spliceIndex) {
        row.element.style.transition = transition;
        row.element.style.transform = `translateY(${(row.currentIndex + insertCount) * ROW_HEIGHT}px)`;
      }
    }

    // 4. New children: start at parent Y (behind it), slide to final Y
    const unused = this.pool.filter((r) => r.currentIndex === -1);
    for (let i = 0; i < insertCount && i < unused.length; i++) {
      const row = unused[i];
      const targetIdx = spliceIndex + i;
      const visible = this.activeView!.getSymbolAtIndex(targetIdx);
      if (!visible) continue;

      // Start state — no transition, behind parent, at parent position
      row.element.style.transition = 'none';
      row.element.style.zIndex = '1';
      row.element.classList.add('vg-anim-opaque');
      row.element.style.transform = `translateY(${parentIndex * ROW_HEIGHT}px)`;

      // Update content
      this.updateRow(row, visible, targetIdx);
      row.currentIndex = targetIdx;
      row.currentSymbolId = visible.symbolId;

      // Force reflow — mandatory to separate start/end states for each child
      void row.element.offsetHeight;

      // End state — transition to final position
      row.element.style.transition = transition;
      row.element.style.transform = `translateY(${targetIdx * ROW_HEIGHT}px)`;
    }

    // 5. Update currentIndex for below-rows (they shifted down)
    for (const row of this.pool) {
      if (row.currentIndex >= spliceIndex && row.currentIndex < spliceIndex + insertCount) continue; // new children already set
      if (row.currentIndex >= spliceIndex) {
        row.currentIndex += insertCount;
      }
    }

    this.updateEmptyState();

    // 6. Cleanup after animation
    setTimeout(() => {
      this.animating = false;
      // Kill all transitions before reconciling so row recycling doesn't flash
      for (const row of this.pool) {
        row.element.style.transition = 'none';
        row.chevronSvg.style.transition = 'none';
        row.subscribeButton.style.transition = 'none';
        row.element.style.zIndex = '';
        row.element.classList.remove('vg-anim-opaque');
      }
      this.scrollContainer.style.transition = '';
      this.renderVisibleRows();
      this.scrollContainer.style.height = `${newHeight}px`;
      // Re-enable CSS transitions next frame (after layout has settled)
      requestAnimationFrame(() => {
        for (const row of this.pool) {
          row.element.style.transition = '';
          row.chevronSvg.style.transition = '';
          row.subscribeButton.style.transition = '';
        }
      });
    }, EXPAND_MS + 20);
  }

  private animateCollapse(parentIndex: number, removeCount: number, newCount: number): void {
    const spliceIndex = parentIndex + 1;
    const newHeight = newCount * ROW_HEIGHT;
    const transition = `transform ${COLLAPSE_MS}ms ${ANIM_EASING}`;

    // 1. Animate container height down (bottom border tracks rows)
    this.scrollContainer.style.transition = `height ${COLLAPSE_MS}ms ${ANIM_EASING}`;
    this.scrollContainer.style.height = `${newHeight}px`;

    // 2. Elevate parent
    const parentRow = this.pool.find((r) => r.currentIndex === parentIndex);
    if (parentRow) {
      parentRow.element.style.zIndex = '2';
      parentRow.element.classList.add('vg-anim-opaque');
      // Update chevron to collapsed state
      const visible = this.activeView!.getSymbolAtIndex(parentIndex);
      if (visible) this.updateRow(parentRow, visible, parentIndex);
    }

    // 3. Children: slide UP to parent Y (behind it)
    // 4. Below rows: slide UP by removeCount * ROW_HEIGHT
    for (const row of this.pool) {
      if (row.currentIndex >= spliceIndex && row.currentIndex < spliceIndex + removeCount) {
        // Collapsing child — opaque so they don't show through each other
        row.element.style.zIndex = '1';
        row.element.classList.add('vg-anim-opaque');
        row.element.style.transition = transition;
        row.element.style.transform = `translateY(${parentIndex * ROW_HEIGHT}px)`;
      } else if (row.currentIndex >= spliceIndex + removeCount) {
        // Below row — slides up to fill the gap
        row.element.style.transition = transition;
        row.element.style.transform = `translateY(${(row.currentIndex - removeCount) * ROW_HEIGHT}px)`;
      }
    }

    this.updateEmptyState();

    // 5. Cleanup after animation
    setTimeout(() => {
      this.animating = false;
      // Kill all transitions before reconciling so row recycling doesn't flash
      for (const row of this.pool) {
        row.element.style.transition = 'none';
        row.chevronSvg.style.transition = 'none';
        row.subscribeButton.style.transition = 'none';
        row.element.style.zIndex = '';
        row.element.classList.remove('vg-anim-opaque');
      }
      this.scrollContainer.style.transition = '';
      this.renderVisibleRows();
      this.scrollContainer.style.height = `${newHeight}px`;
      requestAnimationFrame(() => {
        for (const row of this.pool) {
          row.element.style.transition = '';
          row.chevronSvg.style.transition = '';
          row.subscribeButton.style.transition = '';
        }
      });
    }, COLLAPSE_MS + 20);
  }

  /**
   * Animated expand-all / collapse-all.
   */
  private animateExpandCollapseAll(expanding: boolean): void {
    if (!this.activeView || !this.virtualizer) return;
    this.animating = true;

    // --- 1. Snapshot pool rows BEFORE the toggle (O(POOL_SIZE), not O(N)) ---
    const oldCount = this.activeView.getVisibleCount();
    const poolSnapshot = new Map<string, { index: number; level: number }>();
    for (const row of this.pool) {
      if (row.currentIndex === -1 || row.currentSymbolId === null) continue;
      const vis = this.activeView.getSymbolAtIndex(row.currentIndex);
      poolSnapshot.set(row.currentSymbolId, {
        index: row.currentIndex,
        level: vis?.level ?? 0,
      });
    }

    // --- 2. Apply state change (animating=true blocks onViewChanged) ---
    this.activeView.setExpandAll(expanding);
    this.updateExpandCollapseIcon();

    // --- 3. Read new count ---
    const newCount = this.activeView.getVisibleCount();
    const newHeight = newCount * ROW_HEIGHT;

    // If nothing changed, bail out — still reconcile for chevron state updates
    if (newCount === oldCount) {
      this.animating = false;
      this.scrollContainer.style.height = `${newHeight}px`;
      this.renderVisibleRows();
      return;
    }

    // Update virtualizer count
    this.virtualizer.setOptions({ ...this.virtualizer.options, count: newCount });
    this.virtualizer._willUpdate();

    const duration = expanding ? EXPAND_MS : COLLAPSE_MS;
    const transition = `transform ${duration}ms ${ANIM_EASING}`;

    // --- 4. Pre-build symbolId→index map for O(1) per-row lookup ---
    const indexMap = this.activeView.buildIndexMap();
    const newIndexOf = (symbolId: string): number => {
      return indexMap.get(symbolId) ?? -1;
    };

    // --- 5. Animate container height ---
    this.scrollContainer.style.transition = `height ${duration}ms ${ANIM_EASING}`;
    this.scrollContainer.style.height = `${newHeight}px`;

    if (expanding) {
      // === EXPAND ALL ===
      for (const row of this.pool) {
        if (row.currentIndex === -1 || row.currentSymbolId === null) continue;
        const newIdx = newIndexOf(row.currentSymbolId);
        if (newIdx < 0) continue; // shouldn't happen on expand

        const newY = newIdx * ROW_HEIGHT;
        const vis = this.activeView!.getSymbolAtIndex(newIdx);

        // Parent rows (have children) get z-index 2 so children emerge from behind
        if (vis && vis.hasChildren) {
          row.element.style.zIndex = '2';
          row.element.classList.add('vg-anim-opaque');
        }

        row.element.style.transition = transition;
        row.element.style.transform = `translateY(${newY}px)`;

        // Update row content (chevron state, etc.)
        if (vis) this.updateRow(row, vis, newIdx);
        row.currentIndex = newIdx;
      }

      // Assign spare pool rows to newly-visible symbols near the viewport.
      const scrollEl = this.findScrollParent();
      const scrollTop = scrollEl.scrollTop - this.scrollContainer.offsetTop;
      const vpTop = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN_ROWS);
      const vpBot = Math.min(newCount, vpTop + Math.ceil(scrollEl.clientHeight / ROW_HEIGHT) + OVERSCAN_ROWS * 2);

      const assignedSymbols = new Set<string>();
      for (const row of this.pool) {
        if (row.currentSymbolId !== null && row.currentIndex >= 0) assignedSymbols.add(row.currentSymbolId);
      }

      const unused = this.pool.filter((r) => r.currentIndex === -1 || r.currentSymbolId === null);
      let ui = 0;

      for (let ni = vpTop; ni < vpBot && ui < unused.length; ni++) {
        const vis = this.activeView!.getSymbolAtIndex(ni);
        if (!vis || assignedSymbols.has(vis.symbolId)) continue;

        const row = unused[ui++];
        const finalY = ni * ROW_HEIGHT;

        // Find parent Y: walk backward from ni to find nearest shallower level.
        let parentY = 0;
        const walkLimit = Math.max(0, ni - POOL_SIZE);
        for (let j = ni - 1; j >= walkLimit; j--) {
          const pv = this.activeView!.getSymbolAtIndex(j);
          if (pv && pv.level < vis.level) {
            parentY = j * ROW_HEIGHT;
            break;
          }
        }

        // Start behind parent, no transition
        row.element.style.transition = 'none';
        row.element.style.zIndex = '1';
        row.element.classList.add('vg-anim-opaque');
        row.element.style.transform = `translateY(${parentY}px)`;

        this.updateRow(row, vis, ni);
        row.currentIndex = ni;
        row.currentSymbolId = vis.symbolId;

        void row.element.offsetHeight; // force reflow

        row.element.style.transition = transition;
        row.element.style.transform = `translateY(${finalY}px)`;
      }
    } else {
      // === COLLAPSE ALL ===
      const poolByOldIdx: { symbolId: string; oldIdx: number; level: number }[] = [];
      for (const [symbolId, snap] of poolSnapshot) {
        poolByOldIdx.push({ symbolId, oldIdx: snap.index, level: snap.level });
      }
      poolByOldIdx.sort((a, b) => a.oldIdx - b.oldIdx);

      // Walk sorted pool rows, track the last root's new Y
      let lastRootNewY = 0;
      const parentYForSymbol = new Map<string, number>();
      for (const entry of poolByOldIdx) {
        if (entry.level === 0) {
          const newIdx = newIndexOf(entry.symbolId);
          lastRootNewY = newIdx >= 0 ? newIdx * ROW_HEIGHT : lastRootNewY;
        }
        parentYForSymbol.set(entry.symbolId, lastRootNewY);
      }

      // Track which new indices are covered by existing pool rows (roots that survive)
      const coveredNewIndices = new Set<number>();
      const removedChildRows: PooledRow[] = [];

      for (const row of this.pool) {
        if (row.currentIndex === -1 || row.currentSymbolId === null) continue;
        const symbolId = row.currentSymbolId;
        const newIdx = newIndexOf(symbolId);

        if (newIdx >= 0) {
          // Root — survives, animate to new position
          coveredNewIndices.add(newIdx);
          const newY = newIdx * ROW_HEIGHT;
          row.element.style.zIndex = '2';
          row.element.classList.add('vg-anim-opaque');
          row.element.style.transition = transition;
          row.element.style.transform = `translateY(${newY}px)`;

          const vis = this.activeView!.getSymbolAtIndex(newIdx);
          if (vis) this.updateRow(row, vis, newIdx);
          row.currentIndex = newIdx;
        } else {
          // Removed child — slide to root parent's new Y
          const parentY = parentYForSymbol.get(symbolId) ?? 0;
          row.element.style.zIndex = '1';
          row.element.classList.add('vg-anim-opaque');
          row.element.style.transition = transition;
          row.element.style.transform = `translateY(${parentY}px)`;
          removedChildRows.push(row);
        }
      }

      // Assign removed-child pool rows to roots that aren't in the pool yet.
      const scrollEl = this.findScrollParent();
      const scrollTop = scrollEl.scrollTop - this.scrollContainer.offsetTop;
      const vpTop = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN_ROWS);
      const vpBot = Math.min(newCount, vpTop + Math.ceil(scrollEl.clientHeight / ROW_HEIGHT) + OVERSCAN_ROWS * 2);

      let ri = 0;
      for (let ni = vpTop; ni < vpBot && ri < removedChildRows.length; ni++) {
        if (coveredNewIndices.has(ni)) continue;
        const vis = this.activeView!.getSymbolAtIndex(ni);
        if (!vis) continue;

        const row = removedChildRows[ri++];
        const finalY = ni * ROW_HEIGHT;

        row.element.style.transition = 'none';
        row.element.style.zIndex = '2';
        const oldSnap = poolSnapshot.get(row.currentSymbolId!);
        const startY = oldSnap ? oldSnap.index * ROW_HEIGHT : 0;
        row.element.style.transform = `translateY(${startY}px)`;

        this.updateRow(row, vis, ni);
        row.currentIndex = ni;
        row.currentSymbolId = vis.symbolId;

        void row.element.offsetHeight; // force reflow

        row.element.style.transition = transition;
        row.element.style.transform = `translateY(${finalY}px)`;
      }
    }

    this.updateEmptyState();

    // --- 6. Cleanup after animation ---
    setTimeout(() => {
      this.animating = false;
      for (const row of this.pool) {
        row.element.style.transition = 'none';
        row.chevronSvg.style.transition = 'none';
        row.subscribeButton.style.transition = 'none';
        row.element.style.zIndex = '';
        row.element.classList.remove('vg-anim-opaque');
      }
      this.scrollContainer.style.transition = '';
      this.renderVisibleRows();
      this.scrollContainer.style.height = `${newHeight}px`;
      requestAnimationFrame(() => {
        for (const row of this.pool) {
          row.element.style.transition = '';
          row.chevronSvg.style.transition = '';
          row.subscribeButton.style.transition = '';
        }
      });
    }, duration + 20);
  }

  // ============================================================
  // Update Loops
  // ============================================================

  private startUpdateLoops(): void {
    // Value + Chart update loop using requestAnimationFrame.
    // Charts repaint every frame (~60fps) so the time axis scrolls smoothly.
    const chartUpdateLoop = () => {
      this.updateVisibleValuesAndCharts();
      this.valueUpdateRafId = requestAnimationFrame(chartUpdateLoop);
    };
    this.valueUpdateRafId = requestAnimationFrame(chartUpdateLoop);

    // Structural change detection (labels, subscriptions)
    this.structuralCheckInterval = window.setInterval(() => {
      this.checkStructuralChanges();
    }, 100);

    // Freshness tick. Separate from the 100 ms structural check on purpose: ages
    // only ever change at second granularity, so a 1 s interval does the same work
    // ten times less often.
    this.ageTickInterval = window.setInterval(() => {
      this.refreshAges();
    }, 1000);

    // Scroll position saving — cache offset immediately, debounce localStorage write
    const scrollEl = this.findScrollParent();
    this.scrollSaveHandler = () => {
      try {
        const offset = scrollEl.scrollTop - this.scrollContainer.offsetTop;
        this.lastScrollOffset = Math.round(offset);
      } catch {
        /* ignore */
      }
      // Re-resolve the hovered sparkline — the cursor didn't move but the rows did.
      this.scheduleHoverRefresh();
      // Debounce localStorage write
      if (this.scrollSaveTimeout) clearTimeout(this.scrollSaveTimeout);
      this.scrollSaveTimeout = window.setTimeout(() => {
        this.flushScrollPosition();
        this.scrollSaveTimeout = null;
      }, 150);
    };
    scrollEl.addEventListener('scroll', this.scrollSaveHandler, { passive: true });
  }

  private stopUpdateLoops(): void {
    if (this.valueUpdateRafId) {
      cancelAnimationFrame(this.valueUpdateRafId);
      this.valueUpdateRafId = 0;
    }
    if (this.hoverScrollRaf) {
      cancelAnimationFrame(this.hoverScrollRaf);
      this.hoverScrollRaf = 0;
    }
    if (this.ageTickInterval) {
      clearInterval(this.ageTickInterval);
      this.ageTickInterval = null;
    }
    if (this.structuralCheckInterval) {
      clearInterval(this.structuralCheckInterval);
      this.structuralCheckInterval = null;
    }
    if (this.scrollSaveHandler) {
      const scrollEl = this.findScrollParent();
      scrollEl.removeEventListener('scroll', this.scrollSaveHandler);
      this.scrollSaveHandler = null;
    }
    if (this.scrollSaveTimeout) {
      clearTimeout(this.scrollSaveTimeout);
      this.scrollSaveTimeout = null;
    }
  }

  private updateVisibleValuesAndCharts(): void {
    // Cache chart config once per frame (invariant within a single update pass)
    const chartConfig = this.getChartConfig();

    for (const row of this.pool) {
      if (row.currentSymbolId === null) continue;
      const symbolId = row.currentSymbolId;

      // Update value text (dirty-check raw value before formatting)
      const sym = this.source.getSymbol(symbolId);
      const value = this.source.getValue(symbolId);
      const connected = this.isConnected;
      if (value !== row.cache.lastValue) {
        let newText: string;
        if (value !== null && value !== undefined) {
          newText = formatValue(value, sym?.type || '', sym?.enumValues);
          this.setStaleValue(symbolId, newText);
        } else if (!connected) {
          newText = this.staleValues.get(symbolId) || formatValue(value, sym?.type || '', sym?.enumValues);
        } else {
          newText = formatValue(value, sym?.type || '', sym?.enumValues);
        }
        if (newText !== row.cache.lastFormattedValue) {
          row.valueSpan.textContent = newText;
          row.cache.lastFormattedValue = newText;
        }
        row.cache.lastValue = value;
      }

      // Update chart
      this.updateRowChart(row, symbolId, sym?.type || '', chartConfig);
    }
  }

  /** Write one row's freshness. Structural containers get nothing (they hold no
   *  value), and so does a row that has never reported — absent is not "0s ago".
   *
   *  Gated on the row's TYPE, not on whether it has children: a broker topic row
   *  has both a raw message and flattened fields beneath it, and its message ages
   *  like any other value. See rowValue.ts. */
  private paintAge(row: PooledRow, symbolId: string, type: string): void {
    const el = row.valueAge;
    const ts = carriesValue(type) ? this.source.getValueTs?.(symbolId) : undefined;
    if (ts === undefined || !(ts > 0)) {
      if (el.textContent !== '') {
        el.textContent = '';
        el.removeAttribute('title');
      }
      return;
    }
    const text = formatAge(Date.now() - ts);
    if (el.textContent !== text) el.textContent = text;
    // The exact instant lives in the tooltip: the cell shows a coarse age so it
    // stays narrow and stable, and hovering answers "when, precisely".
    const title = t('grid.lastChangedAt', { time: fmtTime(ts) });
    if (el.title !== title) el.title = title;
  }

  /** Advance every visible row's age. A value that stopped arriving produces no
   *  frames, so without this the age would freeze at whatever it was when the last
   *  value landed — the row would still look live, which is the whole defect.
   *
   *  Text-only, on the existing pooled rows: no re-render, no reflow, no height
   *  change, so it cannot violate "nothing moves that the user did not move". */
  private refreshAges(): void {
    for (const row of this.pool) {
      const id = row.currentSymbolId;
      if (!id) continue;
      this.paintAge(row, id, row.cache.type);
    }
  }

  private checkStructuralChanges(): void {
    if (!this.activeView || this.animating) return;

    const changes = this.source.getChanges(this.lastVersion);
    if (changes.version <= this.lastVersion) return;

    // Check if any visible rows have structural changes
    let needsRefresh = false;
    for (const row of this.pool) {
      if (row.currentSymbolId && changes.changedSymbols.has(row.currentSymbolId)) {
        needsRefresh = true;
        break;
      }
    }

    this.lastVersion = changes.version;
    this.source.clearConsumedChanges();

    if (needsRefresh) {
      // Invalidate and re-render
      for (const row of this.pool) {
        row.currentIndex = -1;
      }
      this.renderVisibleRows();
    }
  }

  // ============================================================
  // Toolbar Helpers
  // ============================================================

  private updateSearchHighlight(): void {
    const value = this.searchInput.value;
    if (!value) {
      this.searchHighlight.innerHTML = '';
      return;
    }

    const tokens = formatSearchInput(value);
    this.searchHighlight.innerHTML = tokens
      .map((t) => {
        const cls = `vg-token-${t.type}`;
        // Escape HTML
        const escaped = t.text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        return `<span class="${cls}">${escaped}</span>`;
      })
      .join('');
  }

  private updateAutocomplete(): void {
    if (!this.searchAutocomplete) return;
    const value = this.searchInput.value;
    const cursorPos = this.searchInput.selectionStart ?? value.length;

    // Only show autocomplete when there's a '/' in the input
    if (!value.includes('/')) {
      this.searchAutocomplete.hide();
      return;
    }

    const rect = this.searchInput.getBoundingClientRect();
    this.searchAutocomplete.update(value, cursorPos, rect);
  }

  private updateExpandCollapseIcon(): void {
    if (!this.activeView) return;
    const expanded = this.activeView.expandAllMode;
    this.expandCollapseBtn.title = expanded ? t('grid.collapseAll') : t('grid.expandAll');
    this.expandCollapseSetExpanded(expanded);
  }

  private updateBulkActionBar(): void {
    const count = this.getSelectedCount();
    const connected = this.isConnected;
    const show = count > 0 && connected;

    if (count > 0) {
      this.selectedCountSpan.textContent = t('grid.selectedCount', { n: formatNumber(count) });
    }
    // Re-translate the bulk button labels (this runs on selection change AND on
    // language switch via refreshI18n).
    for (const { span, key } of this.bulkButtonLabels) span.textContent = t(key);

    // Hide the bulk actions this user may not perform. A bar offering "Lock all"
    // to someone without org:symbol:lock is an error waiting to be clicked, and at
    // select-all scale it would be one error per selected symbol.
    const may = (this.source as { may?: GridPermissions } | undefined)?.may;
    for (const { btn, need } of this.bulkButtons) {
      btn.style.display = may && may[need] === false ? 'none' : '';
    }

    this.bulkActionsWrap.classList.toggle('vg-bulk-visible', show);
    this.selectedCountSpan.style.display = show ? '' : 'none'; // count lives outside the wrap now
  }

  private updateEmptyState(): void {
    const hasSymbols = this.activeView && this.activeView.getVisibleCount() > 0;
    // A FILTERED zero-result is not an empty catalog. Telling a user with 14,015
    // symbols that "symbols will appear once the controller connects" reads as a
    // broken grid — the controller is connected; their search just matched nothing.
    const filtered = !!this.activeView?.getFilter();
    this.emptyTitle.textContent = filtered ? t('grid.noMatches') : t('grid.noSymbolsYet');
    this.emptyDesc.textContent = filtered ? t('grid.noMatchesDesc') : t('grid.noSymbolsYetDesc');
    this.emptyState.style.display = hasSymbols ? 'none' : '';
    this.scrollContainer.style.display = hasSymbols ? '' : 'none';
    // The column header stays visible even when empty (matching the log view) —
    // the empty state sits below it instead of the whole grid disappearing.
    this.headerRow.style.display = '';
  }

  // ============================================================
  // Scroll Persistence
  // ============================================================

  private get storagePrefix(): string {
    return `symbolTree_${this.currentControllerId}`;
  }

  private get scrollPositionKey(): string {
    return `${this.storagePrefix}_scrollPosition`;
  }

  /** Write the cached scroll offset to localStorage (no DOM access needed) */
  private flushScrollPosition(): void {
    if (!this.currentControllerId) return;
    try {
      localStorage.setItem(this.scrollPositionKey, String(this.lastScrollOffset));
    } catch {
      /* ignore */
    }
  }

  private restoreScrollPosition(): void {
    try {
      const stored = localStorage.getItem(this.scrollPositionKey);
      if (!stored) return;
      const offset = parseInt(stored, 10);
      if (isNaN(offset)) return;

      // One more rAF ensures the virtualizer + scroll container are fully laid out
      requestAnimationFrame(() => {
        const scrollEl = this.findScrollParent();
        const target = this.scrollContainer.offsetTop + offset;
        scrollEl.scrollTop = target;
      });
    } catch {
      /* ignore */
    }
  }

  // ============================================================
  // Column Widths Persistence
  // ============================================================

  /** Storage key includes version to invalidate stale column widths from older layouts. */
  private get columnWidthsKey(): string {
    return `${this.storagePrefix}_columnWidths_v3`;
  }

  private saveColumnWidths(): void {
    if (!this.currentControllerId || !this.columnWidths) return;
    try {
      localStorage.setItem(this.columnWidthsKey, JSON.stringify(this.columnWidths));
    } catch {
      /* ignore */
    }
  }

  private restoreColumnWidths(): void {
    try {
      const stored = localStorage.getItem(this.columnWidthsKey);
      if (!stored) return;
      const widths = JSON.parse(stored) as number[];
      if (!Array.isArray(widths) || widths.length === 0) return;
      this.columnWidths = widths;
      this.applyColumnWidths();
    } catch {
      /* ignore */
    }
  }


  // ============================================================
  // Selection Persistence
  // ============================================================

  private saveSelectionDebounced(): void {
    if (this.selectionSaveTimeout) clearTimeout(this.selectionSaveTimeout);
    this.selectionSaveTimeout = window.setTimeout(() => {
      this.saveSelection();
      this.selectionSaveTimeout = null;
    }, 300);
  }

  private saveSelection(): void {
    if (!this.currentControllerId) return;
    try {
      if (this.selectAllActive) {
        localStorage.setItem(
          `${this.storagePrefix}_selectedSymbols`,
          JSON.stringify({ selectAll: true, deselected: [...this.deselectedSymbols] }),
        );
      } else if (this.selectedSymbols.size > 0) {
        localStorage.setItem(`${this.storagePrefix}_selectedSymbols`, JSON.stringify([...this.selectedSymbols]));
      } else {
        localStorage.removeItem(`${this.storagePrefix}_selectedSymbols`);
      }
    } catch {
      /* ignore */
    }
  }

  private restoreSelection(): void {
    try {
      const stored = localStorage.getItem(`${this.storagePrefix}_selectedSymbols`);
      if (!stored) return;
      const parsed = JSON.parse(stored);
      if (parsed && typeof parsed === 'object' && parsed.selectAll) {
        this.selectAllActive = true;
        this.deselectedSymbols.clear();
        if (Array.isArray(parsed.deselected)) {
          for (const id of parsed.deselected) this.deselectedSymbols.add(id);
        }
      } else if (Array.isArray(parsed)) {
        for (const id of parsed) this.selectedSymbols.add(id);
      }
    } catch {
      /* ignore */
    }
  }

  // ============================================================
  // Chart Config Helper
  // ============================================================

  private getChartConfig(): SparklineConfig {
    const config = this.source.getControllerChartConfig();
    return {
      timeWindowMs: config.timeWindowMs,
      sampleRateHz: config.sampleRateHz,
      theme: config.theme || 'frost',
    };
  }

  // ============================================================
  // Column Resize
  // ============================================================

  /** Snapshot current resolved column widths from the DOM as percentages of total width */
  private initColumnWidths(): void {
    const headerCells = Array.from(this.headerRow.children) as HTMLElement[];
    const totalWidth = this.wrapper.clientWidth;
    if (totalWidth <= 0) return;
    this.columnWidths = headerCells.map((cell) => (cell.getBoundingClientRect().width / totalWidth) * 100);
  }

  /** Apply column widths as percentages. */
  private applyColumnWidths(): void {
    if (!this.columnWidths) return;
    const template = this.columnWidths.map((pct) => `${pct}%`).join(' ');
    this.wrapper.style.setProperty('--vg-columns', template);
  }

  /**
   * Start a column-wall drag. `colIndex` is the LEFT column of the boundary;
   * width flows between it and its right neighbour. Triggered from BOTH the header
   * resize handle and grabbing the wall at any row height (columnBoundaryAt).
   */
  private startColumnResize(startClientX: number, colIndex: number): void {
    if (!this.columnWidths) this.initColumnWidths();
    if (!this.columnWidths || colIndex < 0 || !this.resizer) return;

    this.columnResizing = true;
    const totalWidth = this.wrapper.clientWidth;
    const nextIndex = colIndex + 1;
    const startLeftPct = this.columnWidths[colIndex];
    const hasRight = nextIndex < this.columnWidths.length;
    const startRightPct = hasRight ? this.columnWidths[nextIndex] : 0;
    const MIN_COL_PCT = 2; // minimum ~2% of total width

    // The blue wall + drag plumbing live in the shared ColumnResizer; only this
    // percentage math (a delta shared between the column and its right neighbour)
    // is grid-specific. onDrag returns the wall's new left so it follows.
    this.resizer.beginDrag(
      startClientX,
      (deltaPx) => {
        const deltaPct = (deltaPx / totalWidth) * 100;

        let newLeft = startLeftPct + deltaPct;
        let newRight = hasRight ? startRightPct - deltaPct : 0;

        if (newLeft < MIN_COL_PCT) {
          newLeft = MIN_COL_PCT;
          newRight = hasRight ? startLeftPct + startRightPct - MIN_COL_PCT : 0;
        }
        if (hasRight && newRight < MIN_COL_PCT) {
          newRight = MIN_COL_PCT;
          newLeft = startLeftPct + startRightPct - MIN_COL_PCT;
        }

        this.columnWidths![colIndex] = newLeft;
        if (hasRight) this.columnWidths![nextIndex] = newRight;
        this.applyColumnWidths();
        return this.boundaryLeftX(colIndex);
      },
      () => {
        this.columnResizing = false;
        this.hoverBoundary = -1;
        this.saveColumnWidths();
      },
    );
  }

  /**
   * The column boundary (index of the LEFT column) whose right edge is within grab
   * distance of `clientX`, anywhere down the rows. Measured off a LIVE row's cells
   * so it works even when the header is hidden; `clientY` must be inside the rows
   * viewport. -1 if the cursor isn't on a wall.
   */
  private columnBoundaryAt(clientX: number, clientY: number): number {
    const GRAB_PX = 5;
    const headRect = this.headerRow.getBoundingClientRect();
    const scRect = this.scrollContainer.getBoundingClientRect();
    // The wall spans the header titles row down through the rows — grabbable
    // anywhere along it; the toolbar above the header is excluded.
    if (clientY < headRect.top || clientY > scRect.bottom) return -1;
    // Header cells share the --vg-columns template with the rows, so their right
    // edges ARE the column boundaries — and they're always present, even at 0 rows.
    const cells = Array.from(this.headerRow.children) as HTMLElement[];
    for (let i = 0; i < cells.length - 1; i++) {
      const rect = cells[i].getBoundingClientRect();
      if (rect.width <= 0) continue; // skip a collapsed/hidden column edge
      if (Math.abs(clientX - rect.right) <= GRAB_PX) return i;
    }
    return -1;
  }

  /**
   * Grab affordance for the column walls: col-resize cursor + a highlight line at
   * the boundary, shown when the cursor is over a wall at any height. No-op
   * mid-drag (the drag owns the highlight then).
   */
  private setColumnHoverBoundary(colIndex: number): void {
    if (this.columnResizing || colIndex === this.hoverBoundary) return;
    this.hoverBoundary = colIndex;
    // The shared ColumnResizer owns the wall element + the col-resize cursor.
    this.resizer?.setHover(colIndex >= 0 ? this.boundaryLeftX(colIndex) : null);
  }

  /** The wall's left px (relative to the wrapper) for column boundary `colIndex`:
   *  the right edge of that column's header cell (or a live row cell as fallback),
   *  centred on the 1px wall. Fed to the shared ColumnResizer for both hover + drag. */
  private boundaryLeftX(colIndex: number): number {
    const wrapperRect = this.wrapper.getBoundingClientRect();
    // The header cell's right edge IS the boundary and is always present (rows may
    // be empty); fall back to a live row only if the header is ever hidden.
    const ref =
      (this.headerRow.children[colIndex] as HTMLElement | undefined) ??
      (this.pool.find((r) => r.currentIndex >= 0 && r.element.offsetParent !== null)?.element.children[colIndex] as
        | HTMLElement
        | undefined);
    if (!ref) return 0;
    // Center the 2px line ON the 1px wall. The wall sits at viewport
    // [cellRight-1, cellRight]; absolute-left is measured from the wrapper's
    // padding edge (inside its 1px border), so offset by 2.5 (1px border + 1.5px
    // to center the 2px highlight on the wall) → it replaces the wall, not a
    // parallel line beside it.
    return ref.getBoundingClientRect().right - wrapperRect.left - 2.5;
  }

  // ============================================================
  // Column Sort
  // ============================================================

  private handleHeaderSortClick(colId: string): void {
    if (!this.activeView) return;
    this.activeView.setSort(colId);
    this.updateSortIndicators();
    this.renderVisibleRows();
  }

  /** Update sort arrow visibility and direction on header cells */
  private updateSortIndicators(): void {
    const sort = this.activeView?.getSort();
    for (const [colId, arrow] of this.sortArrows) {
      if (sort && sort.column === colId) {
        const isAsc = sort.direction === 'asc';
        arrow.classList.toggle('vg-sort-asc', isAsc);
        arrow.classList.toggle('vg-sort-desc', !isAsc);
      } else {
        arrow.classList.remove('vg-sort-asc', 'vg-sort-desc');
      }
    }
  }

  // ============================================================
  // Sparkline Tooltip
  // ============================================================

  private showSparklineTooltip(clientX: number, clientY: number, canvas: HTMLCanvasElement, symbolId: string): void {
    // Store mouse position + hovered canvas — the update loop re-computes every frame
    this.hoveredCanvas = canvas;
    this.hoverState = { symbolId, clientX, clientY };

    // Do an immediate render so the first frame is instant — match the chart's
    // theme (priority symbols chart one theme ahead).
    const base = this.getChartConfig();
    const isPriority = this.source.getPriority ? this.source.getPriority(symbolId) : false;
    this.renderHoverOverlay(canvas, isPriority ? this.priorityChartConfig(base) : base);
  }

  /**
   * Resolve the sparkline + its CURRENT symbol under a screen point. On mousemove
   * we pass `target` (the event's hit element — fast path); on scroll we pass none
   * and fall back to elementFromPoint. The symbol is read from the row's live
   * dataset, so a recycled pooled canvas resolves to whatever symbol it shows NOW,
   * never a stale id. Tooltip + dot are `pointer-events: none`, so elementFromPoint
   * sees through them to the canvas.
   */
  private hitTestSparklineAt(
    clientX: number,
    clientY: number,
    target?: HTMLElement | null,
  ): { canvas: HTMLCanvasElement; symbolId: string } | null {
    const el = target ?? (document.elementFromPoint(clientX, clientY) as HTMLElement | null);
    if (!el) return null;
    const canvas =
      (el.closest('.vg-sparkline') as HTMLCanvasElement) ||
      (el.tagName === 'CANVAS' ? (el as HTMLCanvasElement) : null);
    if (!canvas) return null;
    const rowEl = canvas.closest('.vg-row') as HTMLElement | null;
    const symbolId = rowEl?.dataset.symbolId;
    return symbolId ? { canvas, symbolId } : null;
  }

  /**
   * On scroll the cursor is stationary but rows slide under it (and pooled canvases
   * recycle to new symbols), so we re-resolve the hovered sparkline at the last
   * cursor position — otherwise the tooltip sticks to the chart that scrolled away.
   * Only acts while a tooltip is already showing; hides it if the cursor now sits
   * over a non-chart area. Deferred a frame (see scheduleHoverRefresh) so the
   * scroll-driven row recycle has settled before we read the dataset.
   */
  private refreshHoverAfterScroll(): void {
    if (!this.lastPointer || !this.hoverState) return;
    const hit = this.hitTestSparklineAt(this.lastPointer.x, this.lastPointer.y);
    if (!hit) {
      this.hideSparklineTooltip();
      return;
    }
    this.showSparklineTooltip(this.lastPointer.x, this.lastPointer.y, hit.canvas, hit.symbolId);
  }

  /** rAF-throttled scroll → hover refresh (at most one re-hit-test per frame). */
  private scheduleHoverRefresh(): void {
    if (this.hoverScrollRaf) return;
    this.hoverScrollRaf = requestAnimationFrame(() => {
      this.hoverScrollRaf = 0;
      this.refreshHoverAfterScroll();
    });
  }

  private hideSparklineTooltip(): void {
    this.hideTooltipElements();
    this.hoveredCanvas = null;
    this.hoverState = null;
  }

  /** Hide tooltip text + dot (but don't clear hover state) */
  private hideTooltipElements(): void {
    if (this.sparklineTooltip) this.sparklineTooltip.style.display = 'none';
    if (this.sparklineDot) this.sparklineDot.style.display = 'none';
  }

  /**
   * Core tooltip rendering — called on mousemove AND on every frame by the update loop.
   */
  private renderHoverOverlay(canvas: HTMLCanvasElement, config: SparklineConfig): void {
    if (!this.hoverState) return;

    const raw = this.source.getSymbolBufferRaw(this.hoverState.symbolId);
    if (!raw || raw.count === 0) {
      this.hideTooltipElements();
      return;
    }

    const canvasRect = canvas.getBoundingClientRect();
    const relX = (this.hoverState.clientX - canvasRect.left) / canvasRect.width;

    // Anchor the cursor→time map at the SAME nowMs the line was drawn with
    // (raw.nowMs = the backend-frame clock), NOT Date.now(). Using Date.now()
    // here put the tooltip's time axis ~|offset| ms (≈150ms) ahead of the
    // line's, so the readout led the curve by a fraction of a second.
    const now = raw.nowMs;
    const timeAtCursor = now - config.timeWindowMs * (1 - relX);

    // Find the insertion point — the first data point with ts >= timeAtCursor
    const insertLogical = binarySearchNearest(raw.timestamps, raw.head, raw.count, raw.capacity, timeAtCursor);
    if (insertLogical < 0) {
      this.hideTooltipElements();
      return;
    }

    // Get the two bracketing data points for interpolation
    const insertIdx = (raw.head + insertLogical) % raw.capacity;
    const insertTs = raw.timestamps[insertIdx];

    let interpValue: number;
    let interpTs: number;
    let pastLastPoint = false;

    const lastLogical = raw.count - 1;
    const lastIdx = (raw.head + lastLogical) % raw.capacity;
    const lastTs = raw.timestamps[lastIdx];

    if (timeAtCursor >= lastTs) {
      // Past the last data point — use last value, track cursor position
      interpValue = raw.values[lastIdx];
      interpTs = timeAtCursor;
      pastLastPoint = true;
    } else if (raw.count < 2) {
      // Only one point — snap to it
      interpValue = raw.values[insertIdx];
      interpTs = insertTs;
    } else {
      // Find left and right neighbors
      let leftLogical: number;
      let rightLogical: number;
      if (insertTs >= timeAtCursor) {
        rightLogical = insertLogical;
        leftLogical = insertLogical > 0 ? insertLogical - 1 : 0;
      } else {
        leftLogical = insertLogical;
        rightLogical = insertLogical < raw.count - 1 ? insertLogical + 1 : raw.count - 1;
      }

      const leftIdx = (raw.head + leftLogical) % raw.capacity;
      const rightIdx = (raw.head + rightLogical) % raw.capacity;
      const leftTs = raw.timestamps[leftIdx];
      const rightTs = raw.timestamps[rightIdx];
      const leftVal = raw.values[leftIdx];
      const rightVal = raw.values[rightIdx];

      if (leftLogical === rightLogical || rightTs === leftTs) {
        interpValue = leftVal;
        interpTs = leftTs;
      } else {
        const t = (timeAtCursor - leftTs) / (rightTs - leftTs);
        const tClamped = Math.max(0, Math.min(1, t));
        interpValue = leftVal + (rightVal - leftVal) * tClamped;
        interpTs = leftTs + (rightTs - leftTs) * tClamped;
      }
    }

    // Check if cursor is too far from any data (skip for right-edge region)
    if (!pastLastPoint) {
      const nearestPhysIdx = (raw.head + insertLogical) % raw.capacity;
      const nearestDist = Math.abs(raw.timestamps[nearestPhysIdx] - timeAtCursor);
      if (nearestDist > config.timeWindowMs * 0.1) {
        this.hideTooltipElements();
        return;
      }
    }

    const secsAgo = ((config.timeWindowMs * (1 - relX)) / 1000).toFixed(1);

    const parent = canvas.parentElement;
    const cssWidth = parent ? parent.clientWidth : canvasRect.width;
    const cssHeight = parent ? parent.clientHeight : canvasRect.height;

    const dataX = cssWidth * (1 - (now - interpTs) / config.timeWindowMs);

    const mmCache = getMinMaxCache(canvas);
    let dataY = cssHeight / 2;
    if (mmCache) {
      let minVal = mmCache.min;
      let maxVal = mmCache.max;
      let range = maxVal - minVal;
      if (range === 0) {
        minVal -= 1;
        maxVal += 1;
        range = 2;
      } else {
        const yPad = range * 0.05;
        minVal -= yPad;
        maxVal += yPad;
        range = maxVal - minVal;
      }
      dataY = cssHeight - ((interpValue - minVal) / range) * cssHeight;
    }

    // Draw crosshair line on canvas (stays within cell bounds)
    drawTooltipOverlay(canvas, dataX);

    // Position the dot indicator (DOM element — escapes cell overflow)
    const lineColor = getThemeLineColor(config.theme);
    if (!this.sparklineDot) {
      this.sparklineDot = document.createElement('div');
      this.sparklineDot.className = 'vg-sparkline-dot';
      document.body.appendChild(this.sparklineDot);
    }
    const dotScreenX = canvasRect.left + (dataX / cssWidth) * canvasRect.width;
    const dotScreenY = canvasRect.top + (dataY / cssHeight) * canvasRect.height;
    this.sparklineDot.style.display = '';
    this.sparklineDot.style.left = `${dotScreenX}px`;
    this.sparklineDot.style.top = `${dotScreenY}px`;
    this.sparklineDot.style.borderColor = lineColor; // Theme-colored outer band

    // Create or update tooltip div
    if (!this.sparklineTooltip) {
      this.sparklineTooltip = document.createElement('div');
      this.sparklineTooltip.className = 'vg-sparkline-tooltip';
      document.body.appendChild(this.sparklineTooltip);
    }

    this.sparklineTooltip.innerHTML =
      `<span class="vg-tooltip-value" style="color:${lineColor}">${Number(interpValue).toFixed(3)}</span>` +
      `<span class="vg-tooltip-time">${t('grid.secondsAgo', { s: secsAgo })}</span>`;
    this.sparklineTooltip.style.display = '';
    this.sparklineTooltip.style.left = `${this.hoverState.clientX + 12}px`;
    this.sparklineTooltip.style.top = `${this.hoverState.clientY - 30}px`;
  }

  // ============================================================
  // Helpers
  // ============================================================

  /** Open the value editor for a writable row (square-pen). The write goes
   *  through the dataSource (the sole backend owner); the editor surfaces the
   *  plugin's per-symbol result (LOCKED etc.) inline. */
  private openValueEditor(poolRow: PooledRow): void {
    const symbolId = poolRow.currentSymbolId;
    const write = this.source.writeValue;
    if (!symbolId || !write) return;
    const dataType = poolRow.cache.type;
    // A row with NO CURRENT VALUE still opens. The editor seeds an EMPTY box for it
    // (openFor treats null/undefined as "nothing to show", never the literal text
    // "undefined") and refuses to save blank text, so the only thing that can be
    // published is what the user typed. That is the deliberate choice for quiet
    // topics on brokers with no retained message: a hand-named subject that exists
    // to be published to would otherwise be uneditable forever. Where a first
    // message genuinely cannot be composed — an MQTT single-field edit with no
    // cached message to merge into — the DRIVER refuses, in prose, and the editor
    // shows that refusal inline.
    if (!this.valueEditor) {
      this.valueEditor = document.createElement('vitesse-value-editor') as VitesseValueEditor;
      this.container.appendChild(this.valueEditor);
    }
    this.valueEditor.openFor({
      symbolId,
      dataType,
      currentValue: poolRow.cache.lastValue,
      writeFn: (v) => write.call(this.source, symbolId, v, dataType),
    });
  }

  /** Resolve the selected symbol ids. Explicit selection is in hand. Select-all
   *  must enumerate the FULL catalog server-side (getAllSymbolPaths) — the grid
   *  only holds the resident window, so iterating it would silently miss every
   *  unloaded symbol. Returns a lightweight id list the worker executor chunks;
   *  the plugin reports per-item failures, so no pre-filter. */
  private async getSelectedPaths(): Promise<string[]> {
    if (!this.selectAllActive) return [...this.selectedSymbols];
    let all: string[];
    if (this.source.getAllSymbolPaths) {
      all = await this.source.getAllSymbolPaths();
    } else {
      // No-backend (stub) source: the resident window is the whole catalog.
      all = [];
      for (const s of this.source.iterateSymbols()) all.push(s.symbol_id);
    }
    return this.deselectedSymbols.size ? all.filter((p) => !this.deselectedSymbols.has(p)) : all;
  }

  /** Run a bulk op over the selection via the worker executor (off the main
   *  thread). Streams progress into the bar; shows ok/failed when done. The rows
   *  refresh themselves as the plugin republishes descriptors → indexer → grid. */
  private async runBulk(op: BulkOp): Promise<void> {
    if (this.bulkInProgress) return;
    const isLock = op === 'lock' || op === 'unlock';
    const fn = isLock ? this.source.bulkSetLocked : this.source.bulkSetPriority;
    if (!fn) return; // stub source / no backend
    const set = op === 'lock' || op === 'prioritize'; // true = apply, false = clear

    this.bulkInProgress = true;
    this.bulkActionsWrap.classList.add('vg-bulk-running');
    // "Preparing…" covers select-all enumeration (the full-catalog fetch); for an
    // explicit selection getSelectedPaths resolves instantly and it never shows.
    this.bulkProgressSpan.textContent = t('grid.bulkPreparing');
    try {
      // Whole-controller fast path: select-all + zero deselections + no active
      // filter ⇒ EVERY symbol. One ApplyToAll RPC instead of enumerating + chunking
      // the whole catalog (a 500k lock becomes one call). Same selection predicate as
      // the select-all UI guard, AND-ed with no-filter. Falls through to the per-id
      // chunked path for explicit picks, deselections, or a filtered select-all.
      if (
        this.selectAllActive &&
        this.deselectedSymbols.size === 0 &&
        !this.source.getFilter?.() &&
        this.source.applyToAll
      ) {
        // bulkProgressSpan already shows "Preparing…" while the single RPC is in flight.
        const outcome = await this.source.applyToAll(op);
        this.bulkProgressSpan.textContent = outcome.ok
          ? t('grid.bulkResult', { ok: formatNumber(outcome.affected), failed: formatNumber(0) })
          : outcome.error || t('grid.bulkResult', { ok: formatNumber(0), failed: formatNumber(0) });
        return;
      }

      const paths = await this.getSelectedPaths();
      if (paths.length > 0) {
        this.bulkProgressSpan.textContent = t('grid.bulkProgress', { done: formatNumber(0), total: formatNumber(paths.length) });
        const onProgress = (p: BulkProgress) => {
          this.bulkProgressSpan.textContent = t('grid.bulkProgress', { done: formatNumber(p.done), total: formatNumber(p.total) });
        };
        const outcome = await fn.call(this.source, paths, set, onProgress);
        this.bulkProgressSpan.textContent = t('grid.bulkResult', { ok: formatNumber(outcome.ok), failed: formatNumber(outcome.failed) });
      }
    } catch (e) {
      this.bulkProgressSpan.textContent = e instanceof Error ? e.message : String(e);
    } finally {
      window.setTimeout(() => {
        this.bulkInProgress = false;
        this.bulkActionsWrap.classList.remove('vg-bulk-running');
        this.bulkProgressSpan.textContent = '';
      }, 1800);
    }
  }

  private findPoolRowByElement(el: HTMLElement): PooledRow | undefined {
    const idx = el.dataset.poolIndex;
    if (idx !== undefined) return this.pool[parseInt(idx, 10)];
    // Fallback: search by dataset
    for (const row of this.pool) {
      if (row.element === el) return row;
    }
    return undefined;
  }
}

// ============================================================
// Utility Functions
// ============================================================

// Permission values → localized. Falls back to the raw token for anything novel.
const PERM_KEYS = { r: 'access.read', w: 'access.write', rw: 'access.readWrite' } as const;
function formatPermission(perm: string): string {
  const key = PERM_KEYS[perm as keyof typeof PERM_KEYS];
  return key ? t(key) : perm;
}

// Logical data type → localized. Falls back to the raw vendor token (e.g. 'time')
// for types outside the known logical set.
const TYPE_KEYS = {
  boolean: 'dataType.boolean',
  integer: 'dataType.integer',
  number: 'dataType.number',
  string: 'dataType.string',
  object: 'dataType.object',
  array: 'dataType.array',
} as const;
function formatType(type: string): string {
  const key = TYPE_KEYS[type as keyof typeof TYPE_KEYS];
  return key ? t(key) : type;
}

// Locale-aware numeric formatting (decimal sign + grouping per language). Uses the
// number's own shortest decimal count so we never round away or pad precision —
// integers get grouping, floats keep exactly the digits JS shows.
function localizeNumber(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  if (Number.isInteger(n)) return formatNumber(n);
  const s = String(n);
  const dot = s.indexOf('.');
  const decimals = dot === -1 ? 0 : s.length - dot - 1;
  return formatNumber(n, { maximumFractionDigits: Math.min(decimals, 20) });
}

/** Display an enum value (string/number/boolean) as a string. */
function getEnumDisplayValue(value: string | number | boolean): string {
  return String(value);
}

function formatValue(value: SymbolValue, type: string, enumValues?: (string | number | boolean)[]): string {
  if (value === null || value === undefined) return t('grid.naValue');

  if (enumValues && enumValues.length > 0) {
    const display = getEnumDisplayValue(value as string | number | boolean);
    const valid = enumValues.some((ev) => ev === value);
    return valid ? display : `⚠️ ${display}`;
  }

  switch (type) {
    case 'boolean':
      return String(value);
    case 'string':
      return `"${value}"`;
    case 'object':
    case 'array': {
      const str = JSON.stringify(value);
      return str.length > 40 ? str.slice(0, 37) + '...' : str;
    }
    case 'integer':
    case 'number':
      return typeof value === 'number' ? localizeNumber(value) : String(value);
    default:
      // Unknown/raw vendor type — still localize if it arrived as a number.
      //
      // This is the branch a broker topic's raw message takes: the integrations
      // service types it "JSON" precisely so both readers pass it through and the
      // row prints the payload exactly as it arrived (topic/flatten.go). Clipped,
      // because a message can be arbitrarily large and this string is written to
      // textContent AND title on every pooled row — see CELL_MAX_CHARS.
      return clipCell(typeof value === 'number' ? localizeNumber(value) : String(value));
  }
}

/**
 * Binary search for the nearest timestamp in a circular buffer.
 * Returns the logical index (0-based from head) of the nearest entry, or -1 if empty.
 * O(log count) instead of O(count).
 */
function binarySearchNearest(
  timestamps: Float64Array,
  head: number,
  count: number,
  capacity: number,
  target: number,
): number {
  if (count === 0) return -1;

  let lo = 0;
  let hi = count - 1;

  // Binary search for insertion point
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    const ts = timestamps[(head + mid) % capacity];
    if (ts < target) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }

  // lo is the first index where ts >= target. Check lo and lo-1 for closest.
  const distLo = Math.abs(timestamps[(head + lo) % capacity] - target);

  if (lo > 0) {
    const distPrev = Math.abs(timestamps[(head + lo - 1) % capacity] - target);
    return distPrev <= distLo ? lo - 1 : lo;
  }

  return lo;
}
